/**
 * Cuisine Configurator
 * Handles: group size, tier selection, step progression,
 * quantity controls, add-ons, date picker, running total,
 * and Shopify Cart API integration.
 *
 * Fully dynamic — reads step types, step count, and configurator
 * title from DOM data attributes set by the Liquid section.
 */
(function() {
  'use strict';

  // ══════════════════════════════════════════
  // ── DOM-DRIVEN CONFIGURATION ──
  // Reads step types and counts from data attributes
  // so the same JS works for any cuisine.
  // ══════════════════════════════════════════

  var configuratorEl = document.querySelector('[data-cuisine-configurator]');
  if (!configuratorEl) {
    console.warn('[Configurator] No configurator element found on page, aborting init.');
    return;
  }

  var totalSteps = parseInt(configuratorEl.dataset.totalSteps, 10) || 5;
  var configuratorTitle = configuratorEl.dataset.configuratorTitle || 'Feast';
  var saucePerTwo = configuratorEl.hasAttribute('data-sauce-per-two');

  // Telemetry: configurator booted on a cuisine page. Captures cuisine
  // (best-effort from the section dataset) so we can group journeys by it.
  if (window.WF && window.WF.event) {
    var cuisineHint = configuratorEl.dataset.cuisine
      || (document.querySelector('[data-cuisine-products]') && document.querySelector('[data-cuisine-products]').dataset.cuisine)
      || (location.pathname.match(/\/pages\/([a-z0-9-]+)/i) || [])[1]
      || null;
    window.WF.event('configurator_started', { cuisine: cuisineHint, totalSteps: totalSteps });
  }

  // Build step type mapping by reading data-step-type from each step div
  // e.g. { 1: 'main', 2: 'side', 3: 'sauce', 4: 'addons', 5: 'date' }
  var stepTypeMap = {};
  var typeStepMap = {};   // reverse: { 'main': 1, 'side': 2, ... }
  var pickerTypes = [];   // ordered list of picker types (exclude 'addons' and 'date')

  document.querySelectorAll('[data-config-step][data-step-type]').forEach(function(stepEl) {
    var stepNum = parseInt(stepEl.dataset.configStep, 10);
    var stepType = stepEl.dataset.stepType;
    stepTypeMap[stepNum] = stepType;
    typeStepMap[stepType] = stepNum;
    if (stepType !== 'addons' && stepType !== 'date') {
      pickerTypes.push(stepType);
    }
  });

  console.log('[Configurator] Step mapping:', stepTypeMap, '| Picker types:', pickerTypes, '| Total steps:', totalSteps);

  // Misconfigured section markup (no [data-config-step][data-step-type] with a
  // non-addons/date type) would silently leave the configurator unbuildable —
  // every step would be a date or addons step, no dish-picker logic would run.
  // Surface this loudly at boot so we can fix the cuisine section rather than
  // shipping a broken page.
  if (pickerTypes.length === 0) {
    console.error('[Configurator] No picker step types found. The cuisine section needs at least one step with data-step-type set to something other than "addons" or "date". Configurator will not function correctly.');
  }

  // ══════════════════════════════════════════
  // ── DATA LOADING ──
  // Fetches product data from Shopify's product API
  // to bypass the 20-product all_products Liquid limit
  // ══════════════════════════════════════════

  var configDataEl = document.querySelector('[data-cuisine-products]');
  var configData = { bundleHandles: {}, addonHandles: {} };
  try {
    configData = configDataEl ? JSON.parse(configDataEl.textContent) : configData;
  } catch (e) {
    console.error('[Configurator] Failed to parse config data:', e);
  }

  // Resolved product data (populated by fetchProductData)
  var productData = { bundles: {}, addons: {}, pickerDishTitles: {} };
  var pricing = { feast: {}, box: {} };
  var dataReady = false;

  // Addon prices — initially populated from Liquid data-price attributes, then
  // overwritten with fetched API prices once fetchProductData completes
  var addonPrices = {};
  document.querySelectorAll('[data-addon][data-price]').forEach(function(card) {
    addonPrices[card.dataset.addon] = parseInt(card.dataset.price, 10) || 0;
  });

  // Fetch a single product from Shopify's public API with one retry
  function fetchProduct(handle) {
    // Use /products/{handle}.js (not .json) because the .json endpoint
    // omits the `available` field entirely — `product.available` is
    // undefined, and `undefined !== false` evaluates true, so every
    // sold-out bundle/dish would silently appear available to the
    // configurator. The .js endpoint includes `available` at both
    // product and variant level and reflects current inventory.
    //
    // Note: .js returns price as integer pence ("11400") while .json
    // returned decimal-pound strings ("114.00"). Normalise to the
    // string-decimal shape so downstream `parseFloat(price) * 100`
    // continues to give pence — keeps the existing call sites working.
    var url = '/products/' + handle + '.js';
    return fetch(url)
      .then(function(res) {
        if (!res.ok) {
          if (res.status >= 500 || res.status === 429) {
            console.warn('[Configurator] Fetch "' + handle + '" returned ' + res.status + ', retrying...');
            return new Promise(function(resolve) { setTimeout(resolve, 1500); })
              .then(function() { return fetch(url); })
              .then(function(retryRes) {
                if (!retryRes.ok) throw new Error('Product "' + handle + '" not found (HTTP ' + retryRes.status + ' after retry)');
                return retryRes.json();
              });
          }
          throw new Error('Product "' + handle + '" not found (HTTP ' + res.status + ')');
        }
        return res.json();
      })
      .then(function(product) {
        if (product && Array.isArray(product.variants)) {
          product.variants.forEach(function(v) {
            if (typeof v.price === 'number') {
              v.price = (v.price / 100).toFixed(2);
            }
          });
        }
        return product;
      });
  }

  // Detect whether bundleHandles is the new path-keyed shape ({feast:{...}, box:{...}})
  // or the legacy flat-by-size shape ({2:{...}, 3:{...}}). The Cart Transform path uses
  // tier-keyed; the FBP/legacy path uses flat.
  var isTierKeyedBundles = configData.bundleHandles && configData.bundleHandles.feast && configData.bundleHandles.box;
  console.log('[Configurator] Bundle structure:', isTierKeyedBundles ? 'tier-keyed (Cart Transform)' : 'flat (legacy)');

  // Helper: does a given group size have at least one bundle loaded?
  // Works for both shapes (tier-keyed checks both tiers; legacy checks flat).
  // Hoisted here so click handlers and pre-load guards can both call it.
  function hasBundleForSize(size) {
    if (isTierKeyedBundles) {
      return !!(productData.bundles.feast && productData.bundles.feast[String(size)]) ||
             !!(productData.bundles.box && productData.bundles.box[String(size)]);
    }
    return !!productData.bundles[String(size)];
  }

  // Resolve the bundle entry for a given tier × size, regardless of structure.
  // Returns null when the bundle is missing entirely.
  function getBundleInfo(tier, size) {
    if (isTierKeyedBundles) {
      var t = tier === 'box' ? 'box' : 'feast';
      return (productData.bundles[t] && productData.bundles[t][String(size)]) || null;
    }
    // Legacy flat structure: both tiers share the same bundle entry per size.
    return productData.bundles[String(size)] || null;
  }

  // Is the bundle for this tier × size both loaded AND currently available?
  //
  // Returns:
  //   true  — bundle loaded and Shopify says available
  //   true  — bundle's init-time fetch failed (stub). We defer to the cart-add
  //           path (which retries the fetch at ATC time and is the authoritative
  //           source of truth). Surfacing "Sold out" UI from a transient init
  //           fetch failure is worse than letting the customer try.
  //   false — bundle not configured for this tier×size at all ("Coming soon")
  //   false — bundle loaded and Shopify says available=false (genuine sold-out)
  function isBundleAvailable(tier, size) {
    var info = getBundleInfo(tier, size);
    if (!info) return false;
    if (info._fetchFailed) return true;
    if (!info.variantId) return false;
    return info.available !== false;
  }

  // Does any tier have an AVAILABLE bundle for this size?
  // Used to decide whether the size pill is selectable at all.
  function hasAvailableBundleForSize(size) {
    return isBundleAvailable('feast', size) || isBundleAvailable('box', size);
  }

  // Load all product data on init
  // Flip a single picker-dish card to its sold-out visual state and zero out
  // any selections the customer had made for it. Matches the Liquid render
  // for sold-out dishes (section/cuisine-feast-builder.liquid:306-326). Used
  // both at init (when product API says unavailable) and reactively when
  // /cart/add returns a sold-out 422 naming the dish.
  function flipPickerCardSoldOut(card) {
    if (!card || card.classList.contains('is-sold-out')) return false;
    card.classList.add('is-sold-out');
    card.dataset.soldOut = 'true';
    card.setAttribute('aria-disabled', 'true');

    var nameEl = card.querySelector('.name');
    if (nameEl && !nameEl.querySelector('.cuisine-badge--sold-out')) {
      var badge = document.createElement('span');
      badge.className = 'cuisine-badge cuisine-badge--sold-out';
      badge.textContent = 'Sold out';
      nameEl.appendChild(document.createTextNode(' '));
      nameEl.appendChild(badge);
    }
    var qtyWrap = card.querySelector('.cuisine-picker-qty-wrap');
    if (qtyWrap) {
      var soldOutMarker = document.createElement('div');
      soldOutMarker.className = 'cuisine-picker-qty cuisine-picker-qty--sold-out';
      soldOutMarker.setAttribute('aria-hidden', 'true');
      soldOutMarker.innerHTML = '<span class="cuisine-picker-soldout-label">Sold out</span>';
      qtyWrap.replaceWith(soldOutMarker);
    }
    card.classList.remove('is-selected');

    // Zero out any in-flight selection so the customer's totals correct themselves
    var type = card.dataset.picker;
    var dish = card.dataset.dish;
    if (type && dish && state.selections[type] && state.selections[type][dish]) {
      setSelection(type, dish, 0);
    }
    return true;
  }

  // Walk every rendered picker-dish card and re-fetch its product via
  // fetchProduct (which now hits /products/{handle}.js — see the comment
  // there). Any card whose variant comes back unavailable gets flipped to
  // sold-out client-side. We also cache product titles so the cart-add 422
  // handler can match Shopify error messages back to the offending card.
  async function refreshPickerDishAvailability() {
    var cards = document.querySelectorAll('[data-picker][data-product-handle]');
    if (!cards.length) return;

    var byHandle = {};
    cards.forEach(function(card) {
      var handle = card.dataset.productHandle;
      if (!handle) return;
      if (!byHandle[handle]) byHandle[handle] = [];
      byHandle[handle].push(card);
    });

    var handles = Object.keys(byHandle);
    var flipped = 0;
    await Promise.all(handles.map(function(handle) {
      return fetchProduct(handle).then(function(product) {
        if (!product) return;
        if (product.title) productData.pickerDishTitles[handle] = product.title;
        if (!product.variants || !product.variants.length) return;

        // If the product itself is unavailable, every card on it is sold out.
        if (product.available === false) {
          byHandle[handle].forEach(function(card) {
            if (flipPickerCardSoldOut(card)) {
              flipped++;
              console.warn('[Configurator] Picker dish flipped (product unavailable):', handle, card.dataset.dish);
            }
          });
          return;
        }

        // Per-variant check: each card may point at a different variant of the
        // same multi-variant product (e.g. Half-Half Texas Sausage → Classic
        // Link card + Cheddar and Jalapeño card share one product handle).
        // Match by data-variant-id-numeric and check only THAT variant's
        // availability. Fall back to variants[0] when the card doesn't
        // declare a specific variant id (single-variant products).
        byHandle[handle].forEach(function(card) {
          var idAttr = card.dataset.variantIdNumeric;
          var variantId = idAttr ? parseInt(idAttr, 10) : null;
          // Warn (don't crash) when a multi-variant product card is missing
          // data-variant-id-numeric — the fallback to variants[0] can incorrectly
          // mark the card sold-out if variants[0] is unavailable but the card's
          // real variant is fine. The Liquid render should always set this attr.
          if (!variantId && product.variants.length > 1) {
            console.warn('[Configurator] Card missing data-variant-id-numeric on multi-variant product — falling back to variants[0]:', handle, card.dataset.dish);
          }
          var variant = variantId
            ? product.variants.find(function(v) { return v.id === variantId; })
            : product.variants[0];
          if (variant && variant.available !== false) return;
          if (flipPickerCardSoldOut(card)) {
            flipped++;
            console.warn('[Configurator] Picker dish flipped (variant unavailable):', handle, card.dataset.dish, variant ? '(variant ' + variant.id + ')' : '(no matching variant on product)');
          }
        });
      }).catch(function(err) {
        console.warn('[Configurator] Failed to re-check picker dish:', handle, err.message);
      });
    }));

    if (flipped > 0) {
      console.log('[Configurator] Re-checked ' + handles.length + ' picker dishes — flipped ' + flipped + ' to sold-out.');
    }
  }

  // Normalize a dish title for fuzzy matching: lowercase, strip
  // parenthetical asides ("(Hot)", "(dairy)"), strip common adjective
  // prefixes ("the", "traditional", "classic", "authentic", "house"),
  // and drop punctuation. This lets us match Shopify's
  //   "Traditional Goan Pork Vindaloo (Hot, but not British Indian Restaurant hot)"
  // against the picker card title
  //   "Goan Pork Vindaloo (Hot, but not too hot)"
  // even though they share no exact substring.
  function normalizeTitleForMatch(s) {
    if (!s) return '';
    return String(s)
      .toLowerCase()
      .replace(/\([^)]*\)/g, ' ')
      .replace(/^\s*(the|traditional|classic|authentic|house|our|wf|original)\s+/g, '')
      .replace(/[^a-z0-9 ]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Pull the dish name out of a Shopify cart-add error like
  //   "The product 'X' is already sold out."
  // Returns null if there's no quoted name (some Shopify errors don't
  // name a product at all — e.g. "Only 3 items were added…").
  function extractQuotedDishName(msg) {
    if (!msg) return null;
    var m = String(msg).match(/[‘’'"]([^‘’'"]+)[‘’'"]/);
    return m ? m[1].trim() : null;
  }

  // Given a Shopify error description like
  //   "The product 'Classic Salt and Pepper Brisket - Fattier' is already sold out."
  // try to find which picker-dish card it's about. Match by quoted title
  // first (Shopify usually wraps the title in single quotes), then by
  // normalized substring against our cached product titles.
  function findPickerCardForSoldOutMessage(msg) {
    if (!msg) return null;
    var candidate = extractQuotedDishName(msg) || String(msg);
    var candidateNorm = normalizeTitleForMatch(candidate);
    if (!candidateNorm) return null;

    var bestHandle = null;
    var bestLen = 0;
    Object.keys(productData.pickerDishTitles).forEach(function(handle) {
      var titleNorm = normalizeTitleForMatch(productData.pickerDishTitles[handle]);
      if (!titleNorm) return;
      // Match either direction: error mentions the full product title, or
      // the title is contained in a longer error string.
      var hit = titleNorm === candidateNorm
             || candidateNorm.indexOf(titleNorm) !== -1
             || titleNorm.indexOf(candidateNorm) !== -1;
      if (hit && titleNorm.length > bestLen) {
        bestHandle = handle;
        bestLen = titleNorm.length;
      }
    });
    if (!bestHandle) return null;
    return document.querySelector('[data-picker][data-product-handle="' + bestHandle + '"]');
  }

  async function fetchProductData() {
    console.log('[Configurator] Fetching product data from Shopify API...');
    var errors = [];

    // Collect every unique bundle handle to fetch (tier-keyed = up to 14, legacy = up to 7).
    var uniqueHandles = {};
    if (isTierKeyedBundles) {
      ['feast', 'box'].forEach(function(tier) {
        Object.keys(configData.bundleHandles[tier]).forEach(function(size) {
          uniqueHandles[configData.bundleHandles[tier][size].handle] = true;
        });
      });
    } else {
      Object.keys(configData.bundleHandles).forEach(function(size) {
        uniqueHandles[configData.bundleHandles[size].handle] = true;
      });
    }

    var productCache = {};
    var handles = Object.keys(uniqueHandles);
    // Parallel fetch — bundles are independent, so we don't need to wait for
    // one before kicking off the next. Drops cold-init time on cuisine pages
    // from ~5-10s (14 sequential awaits × ~400-700ms each) to roughly the
    // slowest single fetch + handshake. Each .catch is local so one bad
    // handle doesn't sink the rest.
    await Promise.all(handles.map(function(handle) {
      return fetchProduct(handle)
        .then(function(product) {
          productCache[handle] = product;
          console.log('[Configurator] Loaded bundle product:', handle);
        })
        .catch(function(e) {
          console.error('[Configurator] Failed to load bundle:', handle, e.message);
          errors.push('Bundle "' + handle + '": ' + e.message);
        });
    }));

    // Build bundle data — same shape (productData.bundles[tier][size] or
    // productData.bundles[size] for legacy) as what was fetched.
    // Stub for tier×size whose init fetch failed (typically Shopify 429 under
    // the burst of ~20 parallel requests on cold init). Registered into
    // productData.bundles so the size pill stays enabled — isBundleAvailable
    // treats _fetchFailed as available, and the cart-add preflight retries
    // the fetch at ATC time. If the retry succeeds, the stub is replaced
    // with the real entry before submission.
    function makeFailedStub(cfg) {
      return { variantId: 0, handle: cfg.handle, quantity: cfg.quantity || 1, available: true, _fetchFailed: true };
    }

    if (isTierKeyedBundles) {
      productData.bundles = { feast: {}, box: {} };
      ['feast', 'box'].forEach(function(tier) {
        Object.keys(configData.bundleHandles[tier]).forEach(function(size) {
          var cfg = configData.bundleHandles[tier][size];
          var product = productCache[cfg.handle];
          if (product && product.variants && product.variants.length > 0) {
            var variant = product.variants[0];
            productData.bundles[tier][size] = {
              variantId: variant.id,
              productId: product.id,
              price: Math.round(parseFloat(variant.price) * 100),
              title: product.title,
              quantity: cfg.quantity || 1,
              handle: cfg.handle,
              available: product.available !== false && variant.available !== false
            };
          } else {
            productData.bundles[tier][size] = makeFailedStub(cfg);
            console.warn('[Configurator] Init fetch failed for', tier, 'size', size, cfg.handle, '— stubbing as available, will retry at ATC');
          }
        });
      });
    } else {
      Object.keys(configData.bundleHandles).forEach(function(size) {
        var cfg = configData.bundleHandles[size];
        var product = productCache[cfg.handle];
        if (product && product.variants && product.variants.length > 0) {
          var variant = product.variants[0];
          productData.bundles[size] = {
            variantId: variant.id,
            productId: product.id,
            price: Math.round(parseFloat(variant.price) * 100),
            title: product.title,
            quantity: cfg.quantity || 1,
            note: cfg.note || '',
            available: product.available !== false && variant.available !== false
          };
        } else {
          var stub = makeFailedStub(cfg);
          stub.note = cfg.note || '';
          productData.bundles[size] = stub;
          console.warn('[Configurator] Init fetch failed for size', size, cfg.handle, '— stubbing as available, will retry at ATC');
        }
      });
    }

    // Fetch addon products in parallel — same rationale as bundles above.
    var addonKeys = Object.keys(configData.addonHandles);
    await Promise.all(addonKeys.map(function(addonKey) {
      var addonHandle = configData.addonHandles[addonKey];
      return fetchProduct(addonHandle)
        .then(function(addonProduct) {
          if (!addonProduct || !addonProduct.variants || addonProduct.variants.length === 0) return;
          var addonVariant = addonProduct.variants[0];
          productData.addons[addonKey] = {
            variantId: addonVariant.id,
            price: Math.round(parseFloat(addonVariant.price) * 100),
            available: addonProduct.available !== false && addonVariant.available !== false,
            handle: addonHandle,
            // Keep every variant so size-scaled extras (the Hosting Pack, whose
            // price is per head and therefore differs for a table of 2 vs 8) can
            // re-resolve to the right one when the group size changes.
            variants: addonProduct.variants.map(function(v) {
              return {
                id: v.id,
                title: v.title,
                price: Math.round(parseFloat(v.price) * 100),
                available: v.available !== false
              };
            }),
            productAvailable: addonProduct.available !== false
          };
          resolveSizeScaledAddon(addonKey);
          // Reflect any availability change in the rendered card (covers the
          // case where Liquid rendered "available" at request time but the
          // product has since flipped to sold-out by the time JS loads).
          if (productData.addons[addonKey].available === false) {
            var addonCardEl = document.querySelector('[data-addon="' + addonKey + '"]');
            if (addonCardEl) {
              addonCardEl.classList.add('is-sold-out');
              addonCardEl.dataset.soldOut = 'true';
              addonCardEl.setAttribute('aria-disabled', 'true');
              var qtyCtrl = addonCardEl.querySelector('.cuisine-addon-qty');
              if (qtyCtrl) qtyCtrl.remove();
            }
            console.warn('[Configurator] Addon flipped sold-out client-side:', addonKey);
          }
          console.log('[Configurator] Loaded addon:', addonKey, '→', addonHandle, productData.addons[addonKey].available ? '' : '(SOLD OUT)');
        })
        .catch(function(e) {
          console.error('[Configurator] Failed to load addon:', addonKey, e.message);
          errors.push('Addon "' + addonKey + '": ' + e.message);
        });
    }));

    // Re-check picker dish availability against Shopify. Liquid renders each
    // card based on dish_variant.available at request time; if a dish has
    // flipped sold-out since render, the card still looks pickable and the
    // customer hits a confusing 422 at /cart/add. Mirror what we already do
    // for addons (above) and flip the card's UI to match Shopify's truth.
    await refreshPickerDishAvailability();

    // Build pricing from the defined pricing table (overrides product-derived prices)
    var bundleCount = Object.keys(productData.bundles).length;
    var addonCount = Object.keys(productData.addons).length;
    if (configData.pricingTable) {
      ['feast', 'box'].forEach(function(tier) {
        if (configData.pricingTable[tier]) {
          Object.keys(configData.pricingTable[tier]).forEach(function(size) {
            pricing[tier][parseInt(size, 10)] = configData.pricingTable[tier][size];
          });
        }
      });
      if (configData.pricingTable.freeShippingThreshold) {
        pricing.freeShippingThreshold = configData.pricingTable.freeShippingThreshold;
      }
      if (configData.pricingTable.shippingValue) {
        pricing.shippingValue = configData.pricingTable.shippingValue;
      }
      if (configData.pricingTable.savingsMaxPerHead) {
        pricing.savingsMaxPerHead = configData.pricingTable.savingsMaxPerHead;
      }
    } else {
      // Fallback: derive from product prices if no pricing table
      Object.keys(productData.bundles).forEach(function(size) {
        var b = productData.bundles[size];
        var qty = b.quantity || 1;
        var totalPence = b.price * qty;
        var perHead = Math.round(totalPence / parseInt(size, 10));
        pricing.feast[parseInt(size, 10)] = perHead;
        pricing.box[parseInt(size, 10)] = perHead;
      });
    }

    // Update addon prices from fetched data; disable addons that failed
    Object.keys(productData.addons).forEach(function(key) {
      addonPrices[key] = productData.addons[key].price;
    });
    // Disable any addon card that failed to fetch
    Object.keys(configData.addonHandles).forEach(function(key) {
      if (!productData.addons[key]) {
        var card = document.querySelector('[data-addon="' + key + '"]');
        if (card) {
          card.style.opacity = '0.4';
          card.style.pointerEvents = 'none';
          console.warn('[Configurator] Disabled addon "' + key + '" — product fetch failed');
        }
        delete addonPrices[key];
      }
    });

    // Validate critical data loaded — count bundles regardless of shape
    var actualBundleCount = isTierKeyedBundles
      ? (Object.keys(productData.bundles.feast || {}).length + Object.keys(productData.bundles.box || {}).length)
      : Object.keys(productData.bundles).length;
    if (actualBundleCount === 0) {
      console.error('[Configurator] CRITICAL: No bundle products loaded. Cart will not work.');
      dataReady = false;
    } else {
      dataReady = true;
    }
    console.log('[Configurator] Product data loaded. Bundles:', bundleCount + ', Addons:', addonCount);
    console.log('[Configurator] Pricing:', pricing);
    if (errors.length > 0) {
      console.warn('[Configurator] Load errors (' + errors.length + '):', errors.join(' | '));
    }

    // Update displayed prices now that we have real data
    updatePricing();

    // Apply availability-aware state to size pills and tier CTAs.
    refreshAvailabilityUI();

    // If current group size has no AVAILABLE bundle, switch to first available size
    if (!hasAvailableBundleForSize(state.groupSize)) {
      var allSizes = isTierKeyedBundles
        ? Object.keys(productData.bundles.feast || {}).concat(Object.keys(productData.bundles.box || {}))
        : Object.keys(productData.bundles);
      var availableSizes = Array.from(new Set(allSizes)).map(Number).filter(hasAvailableBundleForSize).sort(function(a, b) { return a - b; });
      if (availableSizes.length > 0) {
        state.groupSize = availableSizes[0];
        sizePills.forEach(function(p) { p.classList.remove('is-selected'); });
        var targetPill = document.querySelector('[data-size="' + state.groupSize + '"]');
        if (targetPill) targetPill.classList.add('is-selected');
        updateGroupSizeDisplay();
        updatePricing();
        console.log('[Configurator] Auto-selected group size', state.groupSize, '(previous size unavailable)');
      }
    }

    // Enable tier buttons now that data is ready (refreshAvailabilityUI will
    // re-disable them per-tier if the current group size is sold out for that tier).
    document.querySelectorAll('[data-select-tier]').forEach(function(btn) {
      if (dataReady) {
        btn.disabled = false;
        btn.textContent = btn.dataset.originalText || btn.textContent;
        btn.style.opacity = '';
        btn.style.cursor = '';
      } else {
        // Show error state instead of perpetual "Loading..."
        btn.textContent = 'Unavailable — please refresh';
        btn.style.opacity = '0.5';
        btn.style.cursor = 'not-allowed';
      }
    });

    // Final pass: CTA state depends on group size, which may have just been auto-switched
    refreshAvailabilityUI();
    updateBoxCard();
    updateFeastCardImage();

    // Restore any saved mid-build state from a previous session (refresh,
    // accidental nav, etc.). Defer to next tick so all sync handlers
    // (size pill clicks, tier clicks) are fully wired up before we replay.
    // A ?feast= resume link is decoded into the same localStorage slot first,
    // so the one replay path serves both.
    applyFeastParam();
    setTimeout(restorePersistedState, 0);
  }

  // Centralised availability-driven UI refresh.
  // Call after data loads AND whenever group size changes.
  //  - Disables size pills whose bundle is missing/sold-out across all tiers.
  //  - For the currently selected group size, marks each tier card as
  //    "Sold out for X people" when that tier × size is unavailable.
  function refreshAvailabilityUI() {
    if (!dataReady) return;

    // Size pills
    sizePills.forEach(function(pill) {
      var size = parseInt(pill.dataset.size, 10);
      if (size >= 9) return; // 8+ is always the "Speak To Us" pill
      var available = hasAvailableBundleForSize(size);
      var present = hasBundleForSize(size);

      if (available) {
        pill.classList.remove('cuisine-size-pill--unavailable');
        pill.style.opacity = '';
        pill.style.pointerEvents = '';
        pill.removeAttribute('title');
        var existing = pill.querySelector('.cuisine-size-pill__soldout');
        if (existing) existing.remove();
      } else {
        pill.classList.add('cuisine-size-pill--unavailable');
        pill.style.opacity = '0.4';
        pill.style.pointerEvents = 'none';
        pill.classList.remove('is-selected');
        pill.title = present ? 'Sold out' : 'Coming soon';
        // Annotate "Sold out" vs implicit "Coming soon" so customers know which it is.
        if (present && !pill.querySelector('.cuisine-size-pill__soldout')) {
          var tag = document.createElement('span');
          tag.className = 'cuisine-size-pill__soldout';
          tag.textContent = 'Sold out';
          pill.appendChild(tag);
        }
        console.log('[Configurator] Disabled size pill for', size, present ? '— sold out' : '— no bundle product');
      }
    });

    // Tier CTA state for the currently selected group size
    ['feast', 'box'].forEach(function(tier) {
      var card = document.querySelector('[data-tier="' + tier + '"]');
      if (!card) return;
      var cta = card.querySelector('[data-select-tier]');
      if (!cta) return;

      var size = state.groupSize;
      var info = getBundleInfo(tier, size);
      // isBundleAvailable treats _fetchFailed stubs as available so the
      // customer can proceed; the preflight at ATC re-fetches and recovers.
      var available = isBundleAvailable(tier, size);
      var original = cta.dataset.originalText || cta.textContent;
      if (!cta.dataset.originalText) cta.dataset.originalText = original;

      if (available) {
        cta.disabled = false;
        cta.textContent = cta.dataset.originalText;
        cta.classList.remove('is-unavailable');
        cta.style.opacity = '';
        cta.style.cursor = '';
        card.classList.remove('is-tier-unavailable');
      } else {
        cta.disabled = true;
        cta.classList.add('is-unavailable');
        cta.style.cursor = 'not-allowed';
        cta.style.opacity = '0.55';
        card.classList.add('is-tier-unavailable');
        if (info && info.available === false) {
          cta.textContent = 'Sold out for ' + size + ' people';
        } else if (info) {
          cta.textContent = 'Temporarily unavailable';
        } else {
          cta.textContent = 'Coming soon for ' + size + ' people';
        }
      }
    });

    // Immersive: needs the Build Your Own bundle AND the Hosting Pack for this size.
    if (immCard) {
      var immCta = immCard.querySelector('[data-select-immersive]');
      if (immCta) {
        if (!immCta.dataset.originalText) immCta.dataset.originalText = immCta.textContent;
        var immSize = state.groupSize;
        var feastOk = isBundleAvailable('feast', immSize);
        var packOk = immPackAvailable(immSize);
        if (feastOk && packOk) {
          immCta.disabled = false;
          immCta.textContent = immCta.dataset.originalText;
          immCta.classList.remove('is-unavailable');
          immCta.style.opacity = '';
          immCta.style.cursor = '';
          immCard.classList.remove('is-tier-unavailable');
        } else {
          immCta.disabled = true;
          immCta.classList.add('is-unavailable');
          immCta.style.cursor = 'not-allowed';
          immCta.style.opacity = '0.55';
          immCard.classList.add('is-tier-unavailable');
          var feastInfo = getBundleInfo('feast', immSize);
          immCta.textContent = (feastInfo && feastInfo.available === false) || (!packOk && productData.addons[immPackKey])
            ? 'Sold out for ' + immSize + ' people'
            : 'Temporarily unavailable';
        }
      }
    }
  }

  var state = {
    groupSize: 4,
    tier: null,
    currentStep: 1,
    totalSteps: totalSteps,
    selections: {},
    // Map<addonKey, quantity>. Was a Set when addons were single-toggle;
    // upgraded to a Map so customers can buy multiples of each extra.
    addons: new Map(),
    selectedDate: null,
    isAddingToCart: false,
    // Immersive Feast (Step D): Build Your Own + The Hosting Pack. Set when the
    // customer chose the Immersive card; the bundle path itself is 'feast'.
    immersive: false,
    // Set at boot from the same sources as cuisineHint (section dataset →
    // products-data dataset → URL slug). Carried on every event payload so
    // the worker rollup can attribute later funnel steps to a cuisine even
    // if the customer's session_id falls outside the configurator_started
    // window (eg cross-day session resumed via persisted state). Previously
    // referenced in event payloads but never set, so always sent null.
    cuisine: (configuratorEl.dataset.cuisine
      || (document.querySelector('[data-cuisine-products]') && document.querySelector('[data-cuisine-products]').dataset.cuisine)
      || (location.pathname.match(/\/pages\/([a-z0-9-]+)/i) || [])[1]
      || null)
  };

  // ── Immersive Feast (Step D, Oct 2026) ──
  // The third card is Build Your Own plus one Hosting Pack (wf-hosting-pack), the
  // variant for the group size ("For 2" ... "For 8"). Its button is a Build Your
  // Own button marked data-select-immersive: same builder, same bundle, same date
  // and cart path, plus the pack added as an extra. Nothing on the order changes
  // apart from that extra line and the hidden _wf_tier marker.
  var immCard = document.querySelector('[data-tier-immersive]');
  var immPackKey = (immCard && immCard.getAttribute('data-pack-addon')) || configData.immersivePackAddonId || null;

  // Pence for the Hosting Pack at this group size, or null until its variants are known.
  function immPackPrice(size) {
    var rec = immPackKey && productData.addons[immPackKey];
    if (!rec || !rec.variants || !rec.variants.length) return null;
    var v = findVariantForSize(rec.variants, size);
    return v ? v.price : null;
  }
  function immPackAvailable(size) {
    var rec = immPackKey && productData.addons[immPackKey];
    if (!rec || !rec.variants) return false;
    var v = findVariantForSize(rec.variants, size);
    return !!v && v.available !== false && rec.productAvailable !== false;
  }
  // True when what is about to be added really is an Immersive Feast: the card was
  // chosen and the Hosting Pack is still in the basket (it can be taken off again
  // on the extras step, which makes it a plain Build Your Own).
  function isImmersiveOrder() {
    return !!(state.immersive && state.tier === 'feast' && immPackKey && (state.addons.get(immPackKey) || 0) > 0);
  }
  // Path name for telemetry: 'immersive' for the Immersive Feast, else the tier.
  function eventTier() {
    if (state.tier === 'feast' && state.immersive) return 'immersive';
    return state.tier || null;
  }
  // Add one Hosting Pack (never more than one by this route).
  function ensureImmersivePack() {
    if (!immPackKey || (state.addons.get(immPackKey) || 0) > 0) return;
    var card = document.querySelector('[data-addon="' + immPackKey + '"]');
    if (card && (card.classList.contains('is-sold-out') || card.dataset.soldOut === 'true')) return;
    if (card) { setAddonQty(card, 1); return; }
    state.addons.set(immPackKey, 1);
    updateRunningTotal();
  }

  // ── Card foot helpers (shared by all three cards) ──
  // The total reads large ("£110") with "total for 4 people" small beneath it.
  // A space between the two spans keeps textContent readable ("£110 total for 4 people").
  function setTierTotal(el, totalPence, size) {
    if (!el) return;
    el.innerHTML = '<span class="cuisine-tier-total-amt"></span> <span class="cuisine-tier-total-sub"></span>';
    el.firstChild.textContent = formatPrice(totalPence);
    el.lastChild.textContent = 'total for ' + size + ' people';
  }
  // True when the customer has picked a Saturday delivery date (and Saturday is
  // on). Saturday is charged, so the "free delivery" lines must not claim
  // otherwise. Before a date is picked this is false: Friday wording is the default.
  var SAT_LINE = 'Saturday delivery \u00a310 (\u00a34.01 over \u00a365)';
  function satDateSelected() {
    if (!saturdayEnabled || !state.selectedDate) return false;
    var p = state.selectedDate.split('-');
    return new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10)).getDay() === 6;
  }
  // Build Your Own and Immersive carry a one-line delivery note (the box card has its own meter).
  function setTierFree(card, totalPence) {
    var el = card && card.querySelector('[data-tier-free]');
    if (!el) return;
    var threshold = pricing.freeShippingThreshold || 6500;
    if (!(totalPence > 0)) { el.textContent = ''; return; }
    if (satDateSelected()) { el.textContent = SAT_LINE; el.classList.remove('is-free'); return; }
    var free = totalPence >= threshold;
    el.textContent = free ? '\u2714 Free delivery included' : 'Free delivery on orders over ' + formatPrice(threshold);
    el.classList.toggle('is-free', free);
  }

  // ── State persistence (localStorage) ──
  // Save mid-build configurator state so a customer refresh / accidental
  // navigation doesn't wipe out their selections. Per-cuisine key so each
  // cuisine page has its own save. 24-hour TTL so very stale state is
  // discarded. Clear on successful cart-add.
  var STATE_KEY = 'wf_config_state_' + (configData.bundleCuisine || (location.pathname || 'default'));
  var STATE_TTL_MS = 24 * 60 * 60 * 1000;
  var isRestoringState = false;
  var persistTimer = null;

  function persistState() {
    if (isRestoringState) return;
    if (persistTimer) clearTimeout(persistTimer);
    persistTimer = setTimeout(function() {
      try {
        var snap = {
          savedAt: Date.now(),
          groupSize: state.groupSize,
          tier: state.tier,
          selections: state.selections || {},
          // addons is a Map<addonKey, quantity>. Serialise as [[key, qty]...]
          // for JSON. Older saves used Array<addonKey> with implicit qty=1;
          // the restore path below accepts both shapes for backwards compat.
          addons: state.addons ? Array.from(state.addons.entries()) : [],
          selectedDate: state.selectedDate,
          immersive: !!state.immersive,
        };
        localStorage.setItem(STATE_KEY, JSON.stringify(snap));
      } catch (e) {
        // localStorage may be disabled / quota-exceeded — silently ignore.
      }
    }, 250);
  }

  function readPersistedState() {
    try {
      var raw = localStorage.getItem(STATE_KEY);
      if (!raw) return null;
      var s = JSON.parse(raw);
      if (!s || typeof s.savedAt !== 'number') return null;
      if (Date.now() - s.savedAt > STATE_TTL_MS) {
        localStorage.removeItem(STATE_KEY);
        return null;
      }
      return s;
    } catch (e) { return null; }
  }

  function clearPersistedState() {
    if (persistTimer) { clearTimeout(persistTimer); persistTimer = null; }
    try { localStorage.removeItem(STATE_KEY); } catch (e) {}
  }

  // ── Save-your-Feast (resume links) ──
  // The commitment stall: picking a date is the first point a customer must
  // know who's coming, and people who leave to check with guests never come
  // back. This lets them email themselves (or copy) a link that rebuilds
  // their exact Feast. The link is just the persistState() snapshot,
  // base64url-encoded in a ?feast= param — applyFeastParam() decodes it into
  // localStorage and the existing restorePersistedState() replay does ALL
  // the restore work (sold-out handling, expired dates, seal, telemetry
  // guards). Keep the two snapshot shapes in sync.
  var KLAVIYO_COMPANY_ID = 'Wy5dEr'; // public site id (same as klaviyo.js URL)
  var SAFE_KEY_RE = /^[a-z0-9_-]+$/i;

  function buildFeastSnapshot() {
    return {
      savedAt: Date.now(),
      groupSize: state.groupSize,
      tier: state.tier,
      selections: state.selections || {},
      addons: state.addons ? Array.from(state.addons.entries()) : [],
      selectedDate: state.selectedDate,
      immersive: !!state.immersive,
    };
  }

  function encodeFeastSnapshot(snap) {
    var json = JSON.stringify(snap);
    // base64url so the token survives URLs untouched.
    return btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function buildResumeUrl() {
    return location.origin + location.pathname + '?feast=' + encodeFeastSnapshot(buildFeastSnapshot());
  }

  // Decode a ?feast= token into a snapshot, keeping ONLY expected keys with
  // expected shapes. Values end up inside querySelector attribute selectors
  // during restore, so every key is whitelisted to [a-z0-9_-].
  function sanitizeFeastSnapshot(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var snap = { savedAt: Date.now(), selections: {}, addons: [] };
    var gs = parseInt(raw.groupSize, 10);
    if (gs >= 1 && gs <= 99) snap.groupSize = String(raw.groupSize).match(/^\d+\+?$/) ? raw.groupSize : gs;
    if (typeof raw.tier === 'string' && SAFE_KEY_RE.test(raw.tier)) snap.tier = raw.tier;
    if (raw.selections && typeof raw.selections === 'object') {
      Object.keys(raw.selections).forEach(function(type) {
        if (!SAFE_KEY_RE.test(type)) return;
        var src = raw.selections[type];
        if (!src || typeof src !== 'object') return;
        var clean = {};
        Object.keys(src).forEach(function(dish) {
          var qty = parseInt(src[dish], 10);
          if (SAFE_KEY_RE.test(dish) && qty > 0 && qty <= 50) clean[dish] = qty;
        });
        if (Object.keys(clean).length) snap.selections[type] = clean;
      });
    }
    if (Array.isArray(raw.addons)) {
      raw.addons.forEach(function(item) {
        var key = Array.isArray(item) ? item[0] : item;
        var qty = Array.isArray(item) ? (parseInt(item[1], 10) || 1) : 1;
        if (typeof key === 'string' && SAFE_KEY_RE.test(key) && qty > 0 && qty <= 50) {
          snap.addons.push([key, qty]);
        }
      });
    }
    if (typeof raw.selectedDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw.selectedDate)) {
      snap.selectedDate = raw.selectedDate;
    }
    if (raw.immersive === true) snap.immersive = true;
    var hasAnything = snap.groupSize || snap.tier || Object.keys(snap.selections).length || snap.addons.length;
    return hasAnything ? snap : null;
  }

  // Runs before restorePersistedState() on load. A resume link deliberately
  // OVERWRITES any locally saved state — clicking it is explicit intent.
  function applyFeastParam() {
    var m = (location.search || '').match(/[?&]feast=([A-Za-z0-9_-]+)/);
    if (!m) return;
    try {
      var b64 = m[1].replace(/-/g, '+').replace(/_/g, '/');
      var json = decodeURIComponent(escape(atob(b64)));
      var snap = sanitizeFeastSnapshot(JSON.parse(json));
      if (!snap) return;
      localStorage.setItem(STATE_KEY, JSON.stringify(snap));
      // Strip the token so a later refresh doesn't clobber newer edits.
      try { history.replaceState(null, '', location.pathname + location.hash); } catch (e2) {}
      if (window.WF && window.WF.event) {
        window.WF.event('feast_resumed', { cuisine: configData.bundleCuisine || null, tier: snap.tier || null });
      }
      console.log('[Configurator] Applied saved Feast from resume link');
    } catch (e) {
      console.warn('[Configurator] Bad feast resume token — ignoring.');
    }
  }

  function wireFeastSave() {
    var box = document.querySelector('[data-feast-save]');
    if (!box) return;
    var emailInput = box.querySelector('[data-feast-save-email]');
    var sendBtn = box.querySelector('[data-feast-save-send]');
    var copyBtn = box.querySelector('[data-feast-save-copy]');
    var statusEl = box.querySelector('[data-feast-save-status]');

    function setStatus(msg, ok) {
      if (!statusEl) return;
      statusEl.textContent = msg;
      statusEl.hidden = false;
      statusEl.classList.toggle('is-ok', !!ok);
    }

    if (copyBtn) copyBtn.addEventListener('click', function() {
      var url = buildResumeUrl();
      var done = function() {
        setStatus('Link copied — paste it anywhere, your Feast rebuilds itself.', true);
        if (window.WF && window.WF.event) window.WF.event('feast_saved', { method: 'copy', cuisine: configData.bundleCuisine || null, tier: state.tier || null });
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(done, function() { window.prompt('Copy your Feast link:', url); done(); });
      } else {
        window.prompt('Copy your Feast link:', url);
        done();
      }
    });

    if (sendBtn) sendBtn.addEventListener('click', function() {
      var email = (emailInput && emailInput.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        setStatus('Please enter a valid email address.', false);
        if (emailInput) emailInput.focus();
        return;
      }
      sendBtn.disabled = true;
      var url = buildResumeUrl();
      fetch('https://a.klaviyo.com/client/events/?company_id=' + KLAVIYO_COMPANY_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2025-01-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: {
                resume_url: url,
                cuisine: configData.bundleCuisine || null,
                group_size: state.groupSize || null,
                tier: state.tier || null,
              },
              metric: { data: { type: 'metric', attributes: { name: 'Saved Feast' } } },
              profile: { data: { type: 'profile', attributes: { email: email } } },
            },
          },
        }),
      }).then(function(res) {
        sendBtn.disabled = false;
        if (res.ok || res.status === 202) {
          setStatus('Sent — check your inbox. Your Feast link works any time.', true);
          if (window.WF && window.WF.event) window.WF.event('feast_saved', { method: 'email', cuisine: configData.bundleCuisine || null, tier: state.tier || null });
        } else {
          setStatus("Couldn't send just now — use Copy link instead.", false);
        }
      }).catch(function() {
        sendBtn.disabled = false;
        setStatus("Couldn't send just now — use Copy link instead.", false);
      });
    });
  }
  wireFeastSave();

  // Restore previously-saved state by replaying the user actions via the
  // existing click handlers — that keeps the state machine consistent
  // (handlers do validation, UI updates, etc.). isRestoringState guards
  // against the resulting state changes triggering another save.
  function restorePersistedState() {
    var saved = readPersistedState();
    if (!saved) return;
    isRestoringState = true;
    try {
      // Group size first (drives everything else)
      if (saved.groupSize && saved.groupSize !== state.groupSize) {
        var pill = document.querySelector('.cuisine-size-pill[data-size="' + saved.groupSize + '"]');
        if (pill && !pill.classList.contains('cuisine-size-pill--unavailable')) {
          pill.click();
        }
      }
      // Only auto-resume INTO the builder when the customer actually started
      // building — dishes picked, add-ons, or a delivery date. A tier tap alone
      // isn't worth skipping the Feast/Immersive choice for: it drops returning
      // visitors straight into the sealed builder (they never chose a tier this
      // visit), which reads as broken. With no real progress the size pill is
      // already restored above — stop here and let them choose a tier normally.
      var hasProgress = false;
      if (saved.selections && typeof saved.selections === 'object') {
        hasProgress = Object.keys(saved.selections).some(function(type) {
          var ts = saved.selections[type] || {};
          return Object.keys(ts).some(function(d) { return (parseInt(ts[d], 10) || 0) > 0; });
        });
      }
      if (!hasProgress && Array.isArray(saved.addons)) {
        // The Hosting Pack the Immersive card added by itself is not progress
        // (same as a bare tier tap), only extras the customer chose.
        hasProgress = saved.addons.some(function(item) {
          var k = Array.isArray(item) ? item[0] : item;
          var q = Array.isArray(item) ? (parseInt(item[1], 10) || 1) : 1;
          return !(saved.immersive && k === immPackKey && q <= 1);
        });
      }
      if (!hasProgress && saved.selectedDate) hasProgress = true;
      if (!hasProgress) return; // finally{} still resets isRestoringState

      // Tier (this opens the configurator)
      if (saved.tier) {
        var tierBtn = (saved.immersive && saved.tier === 'feast' && immCard)
          ? immCard.querySelector('[data-select-immersive]:not([disabled])')
          : document.querySelector('[data-tier="' + saved.tier + '"] [data-select-tier]:not([disabled])');
        if (tierBtn) tierBtn.click();
      }
      // Picker dish selections — replay +/- clicks per dish
      if (saved.selections && typeof saved.selections === 'object') {
        Object.keys(saved.selections).forEach(function(type) {
          var typeSel = saved.selections[type] || {};
          Object.keys(typeSel).forEach(function(dish) {
            var qty = parseInt(typeSel[dish], 10) || 0;
            if (qty <= 0) return;
            var card = document.querySelector('[data-picker="' + type + '"][data-dish="' + dish + '"]');
            if (!card || card.classList.contains('is-sold-out')) return;
            var plus = card.querySelector('[data-qty-plus]');
            if (!plus) return;
            for (var i = 0; i < qty; i++) plus.click();
          });
        });
      }
      // Addons. Accepts both new shape (Array<[key, qty]>) and old (Array<key>
      // with implicit qty=1) so a customer mid-session across the upgrade
      // doesn't lose their picks. Replay by clicking the + button qty times,
      // which keeps the UI/state in sync through the existing handler.
      if (Array.isArray(saved.addons)) {
        saved.addons.forEach(function(item) {
          var addonKey, qty;
          if (Array.isArray(item)) { addonKey = item[0]; qty = item[1] || 1; }
          else { addonKey = item; qty = 1; }
          var addonCard = document.querySelector('[data-addon="' + addonKey + '"]');
          if (!addonCard || addonCard.classList.contains('is-sold-out')) return;
          var plus = addonCard.querySelector('[data-addon-qty-plus]');
          if (!plus) return;
          for (var i = 0; i < qty; i++) plus.click();
        });
      }
      // Delivery date — render the Friday cards, then click the matching card
      // so selectDate() handles all the side effects (state, labels, footer
      // button). If the saved date is further out than the default window,
      // expand the list until its card is present.
      if (saved.selectedDate) {
        var dateParts = saved.selectedDate.split('-');
        if (dateParts.length === 3) {
          var d = new Date(parseInt(dateParts[0], 10), parseInt(dateParts[1], 10) - 1, parseInt(dateParts[2], 10));
          // Only restore if the saved date is still in the future
          if (!isNaN(d.getTime()) && d.getTime() > Date.now() - 24*60*60*1000) {
            if (typeof renderCalendar === 'function') renderCalendar();
            var dayCell = document.querySelector('[data-fri-list] [data-date="' + saved.selectedDate + '"]');
            // Saved date sits beyond the shown window — expand and re-render.
            var expandGuard = 0;
            while (!dayCell && expandGuard < 12) {
              datesShown += DATES_STEP;
              if (typeof renderCalendar === 'function') renderCalendar();
              dayCell = document.querySelector('[data-fri-list] [data-date="' + saved.selectedDate + '"]');
              expandGuard++;
            }
            if (dayCell) {
              // Click the card so selectDate() fires its full side-effect chain
              dayCell.click();
            } else {
              // Saved date is no longer pickable (passed the cutoff, hit a
              // blackout, or is beyond the scan horizon). Don't ghost-set
              // state.selectedDate — that would leave the date-step UI looking
              // empty while addToCart still proceeds. Reset the window and
              // force the customer to re-pick a current date.
              datesShown = 4;
              if (typeof renderCalendar === 'function') renderCalendar();
              console.log('[Configurator] Saved date', saved.selectedDate, 'no longer pickable — dropping from restore.');
            }
          }
        }
      }
      console.log('[Configurator] Restored saved state from localStorage');
    } catch (e) {
      console.warn('[Configurator] Failed to restore saved state:', e.message);
    } finally {
      isRestoringState = false;

      // Catch any saved picks that have gone sold-out since they were saved.
      // The restore loop above already skips dishes whose card is sold-out at
      // restore-time, but a pick could have been saved under a `data-dish` key
      // that no longer maps to a rendered card, or the recheck could have
      // missed an edge case. Drop anything stale and tell the customer once,
      // up-front, rather than waiting for them to hit Add to Cart.
      var soldOutOnRestore = findSoldOutPicks();
      if (soldOutOnRestore.length) {
        console.warn('[Configurator] Dropping stale picks from restored state:', soldOutOnRestore);
        soldOutOnRestore.forEach(function(p) { dropPickAndRefreshCard(p.type, p.dish); });
        if (typeof updateRunningTotal === 'function') updateRunningTotal();
        if (typeof refreshAvailabilityUI === 'function') refreshAvailabilityUI();
        var labels = soldOutOnRestore.map(function(p) { return p.label; });
        var msg = labels.length === 1
          ? 'Heads up — ' + labels[0] + ' has sold out since you last saved this Feast. Please pick a replacement.'
          : 'Heads up — these dishes have sold out since you last saved this Feast: ' + labels.join(', ') + '. Please pick replacements.';
        // Delay the modal slightly so it doesn't fight the page's own init animations.
        setTimeout(function() {
          showWfModal(msg, { title: 'Your saved Feast needs an update', cta: 'Got it' });
        }, 600);
      }

      // One persist after restore to capture the validated/normalised state.
      persistState();
    }
  }

  // ── Post-add UI refresh ──
  // We update the header cart icon + show the add-to-cart toast ourselves
  // because:
  // (1) Shopify's shop-cart-sync ESM module loads after the inline atc-toast
  //     snippet and overwrites window.fetch — atc-toast's fetch interceptor
  //     never fires, so its toast and its slidecart suppression are both dead.
  // (2) The configurator does its own /cart/add.js call; it doesn't go through
  //     Dawn's product-form.js, which is what normally publishes cartUpdate
  //     and re-renders the header cart bubble.
  // The result without these helpers: the AJAX add succeeds silently — no
  // toast, no header count change, customer can't tell it worked, clicks
  // again, ends up with double.
  function updateHeaderCartBubble(sectionsResponse) {
    if (!sectionsResponse) return;
    var bubbleHTML = sectionsResponse['cart-icon-bubble'];
    if (!bubbleHTML) return;
    var oldLink = document.getElementById('cart-icon-bubble');
    if (!oldLink) return;
    try {
      var doc = new DOMParser().parseFromString(bubbleHTML, 'text/html');
      // The returned HTML is the inner contents of the cart-icon-bubble section
      // — i.e. the contents of the <a id="cart-icon-bubble"> in header.liquid.
      // Replace the old anchor's inner HTML so its event listeners (if any)
      // and outer wiring survive.
      oldLink.innerHTML = doc.body.innerHTML;
    } catch (e) {
      console.warn('[Configurator] Failed to update header cart bubble:', e.message);
    }
  }

  // Refresh the AMP drawer's cart state, then open it. Falls back to opening
  // straight away if the update call is missing or its callback never fires.
  function openSlideCartAfterAdd() {
    var opened = false;
    function openNow() {
      if (opened) return;
      opened = true;
      try {
        if (typeof window.SLIDECART_OPEN === 'function') window.SLIDECART_OPEN();
        else if (typeof window.SLIDECART_UPDATED === 'function') window.SLIDECART_UPDATED();
      } catch (e) { /* never block the add-to-cart flow */ }
      // No extra telemetry: AMP then fires SLIDECART_OPENED, which already
      // sends slidecart_opened (snippets/amp-slidecart.liquid).
    }
    try {
      if (typeof window.SLIDECART_UPDATE === 'function') {
        window.SLIDECART_UPDATE(openNow);
        setTimeout(openNow, 1200);
        return;
      }
    } catch (e) { /* fall through */ }
    openNow();
  }

  function showAddToCartToast(data) {
    var toast = document.getElementById('wf-atc-toast');
    if (!toast) return; // toast snippet not rendered (atc_behavior=slidecart)
    var item = null;
    if (data && Array.isArray(data.items) && data.items.length) {
      item = data.items[data.items.length - 1];
    } else if (data && (data.product_title || data.title)) {
      item = data;
    }
    if (!item) return;
    var titleEl = toast.querySelector('.wf-atc-toast__title');
    var imgEl = toast.querySelector('.wf-atc-toast__image');
    if (titleEl) titleEl.textContent = item.product_title || item.title || 'Item added';
    if (imgEl) {
      // Shopify returns featured_image as an OBJECT even when the product has no
      // image — { url: null, width: null, ... }. The old `|| item.featured_image`
      // fallback (meant for the case where it is a plain string) therefore
      // resolved to the object itself, String()'d to "[object Object]", and set
      // that as the img src. Every bundle add-to-cart showed a broken-image
      // icon at the exact moment of purchase, on the Feast and Immersive as
      // well as the box — no bundle product has ever carried an image.
      // Only a non-empty string is a usable src.
      var raw = item.image || (item.featured_image && item.featured_image.url) || item.featured_image;
      var src = (typeof raw === 'string' && raw) ? raw : null;
      if (src) {
        imgEl.src = String(src).replace(/(\.(?:jpg|jpeg|png|webp)).*$/i, '$1?width=120');
        imgEl.hidden = false;
      } else {
        imgEl.removeAttribute('src');
        imgEl.hidden = true;
      }
    }
    toast.hidden = false;
    void toast.offsetWidth; // force reflow so the transition fires
    toast.classList.add('is-open');
    setTimeout(function() {
      toast.classList.remove('is-open');
      setTimeout(function() {
        if (!toast.classList.contains('is-open')) toast.hidden = true;
      }, 320);
    }, 6000);
  }

  // ── Zapiet date sync ──
  // After add-to-cart, auto-populate Zapiet's delivery date input
  // so the customer doesn't have to pick the same date twice.
  // Zapiet's input is React-controlled, so we go through the native
  // value-setter (so React's tracker registers the change) and also
  // poll because Zapiet destroys and re-mounts its DOM ~2-3s after open.
  function syncZapietDate(isoDate) {
    if (!isoDate) return;
    var parts = isoDate.split('-');
    var date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    var days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    var formatted = days[date.getDay()] + ', ' + months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear();
    var iso = isoDate;

    var nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;

    var startTime = Date.now();
    var maxDuration = 15000;
    var stableCount = 0;

    var sawInput = false;
    var poll = setInterval(function() {
      if (Date.now() - startTime > maxDuration) {
        clearInterval(poll);
        if (!sawInput) {
          console.warn('[Configurator] Zapiet input never appeared after ' + maxDuration + 'ms — date sync failed. Check whether Zapiet selectors (#Zapiet-InputCalendar__shipping/__delivery) have changed.');
        }
        return;
      }

      var visible = document.querySelector('#Zapiet-InputCalendar__shipping') ||
                    document.querySelector('#Zapiet-InputCalendar__delivery');
      var hidden = document.querySelector('#Zapiet-InputCalendar__shippingDate_ISO') ||
                   document.querySelector('#Zapiet-InputCalendar__deliveryDate_ISO');

      if (!visible || visible.offsetParent === null) {
        stableCount = 0;
        return;
      }
      sawInput = true;

      if (visible.value === formatted) {
        stableCount++;
        if (stableCount >= 5) {
          clearInterval(poll);
        }
        return;
      }

      nativeSetter.call(visible, formatted);
      visible.dispatchEvent(new Event('input', { bubbles: true }));
      visible.dispatchEvent(new Event('change', { bubbles: true }));
      if (hidden) {
        nativeSetter.call(hidden, iso);
        hidden.dispatchEvent(new Event('input', { bubbles: true }));
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, 500);
  }

  // ── Formatting ──
  function formatPrice(pence) {
    var pounds = pence / 100;
    return '£' + (pounds % 1 === 0 ? pounds : pounds.toFixed(2));
  }

  function getDishLabel(dish) {
    var card = document.querySelector('[data-dish="' + dish + '"]');
    if (card) {
      var nameEl = card.querySelector('.name');
      if (nameEl) return nameEl.textContent.replace(/Vegan|Veggie/g, '').trim();
    }
    return dish.replace(/-/g, ' ');
  }

  // ══════════════════════════════════════════
  // ── GROUP SIZE ──
  // ══════════════════════════════════════════

  var sizePills = document.querySelectorAll('[data-cuisine-sizes] .cuisine-size-pill');
  // Make the <div> pills keyboard-accessible without changing the Liquid
  // markup. Add role + tabindex + Space/Enter handler so keyboard users
  // can navigate and activate them like real buttons. aria-pressed is
  // maintained per-click below so screen readers announce selection state.
  sizePills.forEach(function(pill) {
    if (!pill.hasAttribute('role')) pill.setAttribute('role', 'button');
    if (!pill.hasAttribute('tabindex')) pill.setAttribute('tabindex', '0');
    if (!pill.hasAttribute('aria-pressed')) {
      pill.setAttribute('aria-pressed', pill.classList.contains('is-selected') ? 'true' : 'false');
    }
    pill.addEventListener('keydown', function(e) {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        pill.click();
      }
    });
  });
  sizePills.forEach(function(pill) {
    pill.addEventListener('click', function() {
      var size = parseInt(this.dataset.size, 10);
      if (size >= 9) {
        showWfModal(
          'For groups over 8, the only constraint is your preparation space. Let us help you build the right Feast for your space and budget.',
          { title: 'Let\'s Speak To Us', cta: 'Speak To Us →', ctaHref: '/pages/contact' }
        );
        return;
      }
      // Guard against selecting a size with no AVAILABLE bundle (missing or sold-out).
      // Catches both legacy flat-by-size and tier-keyed structures.
      if (dataReady && !hasAvailableBundleForSize(size)) {
        return;
      }
      state.groupSize = size;
      // Don't emit telemetry when restorePersistedState() is replaying clicks —
      // it would log restored funnel actions as fresh user actions and inflate
      // size_selected / tier_selected / etc. counts on returning visitors.
      if (!isRestoringState && window.WF && window.WF.event) window.WF.event('size_selected', { size: size, cuisine: state.cuisine || null });
      sizePills.forEach(function(p) { p.classList.remove('is-selected'); });
      this.classList.add('is-selected');
      updateGroupSizeDisplay();
      updatePricing();
      refreshAvailabilityUI();
      // If the currently selected tier just became unavailable at the new size,
      // gently close the configurator so the customer is forced to re-pick a tier
      // rather than building a Feast they can't add.
      if (state.tier && !isBundleAvailable(state.tier, state.groupSize)) {
        console.log('[Configurator] Tier', state.tier, 'unavailable at size', size, '— closing configurator');
        closeConfigurator();
      } else {
        resetSelections();
        validateAllSteps();
      }
      // Keep aria-pressed in sync with the visual is-selected state.
      sizePills.forEach(function(p) {
        p.setAttribute('aria-pressed', p.classList.contains('is-selected') ? 'true' : 'false');
      });
      console.log('[Configurator] Group size changed to', size);
      persistState();
    });
  });

  function updateGroupSizeDisplay() {
    document.querySelectorAll('[data-group-size]').forEach(function(el) {
      el.textContent = state.groupSize;
    });
    document.querySelectorAll('[data-sauce-count]').forEach(function(el) {
      el.textContent = Math.ceil(state.groupSize / 2);
    });
    // Tier-card "Minimum feast" total weight scales with head count
    // (min_per_head × heads); per-head stays constant. The Liquid renders a
    // fixed default, so without this the total read a frozen 1.53kg no matter
    // which size was selected (owner-reported). Runs on init + every size change.
    var minPh = (configData && configData.minPerHead) || 650;
    document.querySelectorAll('.cuisine-tier-minweight').forEach(function(el) {
      var totalG = minPh * (state.groupSize || 0);
      if (totalG <= 0) return;
      var kg = totalG / 1000;
      var kgStr = (totalG % 1000 === 0 ? kg.toFixed(0) : kg.toFixed(2)) + 'kg';
      el.innerHTML = 'Minimum feast <strong>' + kgStr + '</strong> · <strong>' + minPh + 'g</strong> of food per head';
    });
  }

  function updatePricing() {
    resolveAllSizeScaledAddons();
    // Small-screen price readout under the size buttons. Same pricing table as
    // the cards, so the two can never disagree; a column whose card is absent
    // stays hidden rather than showing a price for a path this cuisine lacks.
    var readout = document.querySelector('[data-size-readout]');
    if (readout) {
      var anyCol = false;
      readout.querySelectorAll('[data-readout-tier]').forEach(function(col) {
        var tier = col.dataset.readoutTier;
        var ph = pricing[tier] && pricing[tier][state.groupSize];
        var cardExists = !!document.querySelector('[data-tier="' + tier + '"]');
        if (!ph || !cardExists) { col.hidden = true; return; }
        col.hidden = false;
        anyCol = true;
        var t = col.querySelector('[data-readout-total]');
        var u = col.querySelector('[data-readout-unit]');
        if (t) t.textContent = formatPrice(ph * state.groupSize);
        if (u) u.textContent = 'total \u00b7 ' + formatPrice(ph) + '/head';
      });
      readout.hidden = !anyCol;
      readout.setAttribute('aria-hidden', anyCol ? 'false' : 'true');
    }

    // Update both fork cards with per-head price, totals, and savings
    ['feast', 'box'].forEach(function(tier) {
      var card = document.querySelector('[data-tier="' + tier + '"]');
      if (!card) return;

      var perHead = pricing[tier][state.groupSize];
      if (!perHead) return;

      // Owner's rule: show the discount against a FIXED max price (£25/head),
      // not against each path's own 2-person price — so the saving grows with
      // the group on both cards and the two paths stay comparable. Falls back
      // to the old per-path baseline if the setting is cleared.
      var maxPerHead = pricing.savingsMaxPerHead || pricing[tier][2];

      var amountEl = card.querySelector('.cuisine-tier-price-amount');
      if (amountEl) amountEl.textContent = formatPrice(perHead);

      // The group size is shown in the total just above, so the unit is just "/ per head".
      var unitEl = card.querySelector('.cuisine-tier-price-unit');
      if (unitEl) unitEl.textContent = '/ per head';

      // Ben (2026-09-01): the full price sits with the per-head price on every
      // card. Same pricing table, so it can never disagree with the per-head
      // figure or the mobile readout.
      setTierTotal(card.querySelector('[data-tier-price-total]'), perHead * state.groupSize, state.groupSize);
      setTierFree(card, perHead * state.groupSize);

      // Per-guest counts ("4 packs of menu cards", "4 branded napkins" etc.)
      // — keep these in lock-step with the chosen group size so the card
      // doesn't claim "4 napkins" while the customer is configuring for 6.
      card.querySelectorAll('[data-tier-extra-count]').forEach(function(el) {
        el.textContent = state.groupSize;
      });

      // (The "From \u00a3X for 2 \u00b7 \u2026" price-total line was removed from the tier
      // cards per owner \u2014 duplicated info. No element to update here now.)

      // Savings display: roll per-head discount vs max price together with
      // shipping savings once the cart qualifies for free delivery, so the
      // customer sees one combined "you save \u00a3X" number that updates
      // dynamically as they change group size / tier.
      var savingsEl = card.querySelector('[data-tier-savings]');
      if (savingsEl) {
        var totalCost = perHead * state.groupSize;
        var totalSaved = 0;
        var includesShipping = false;

        // Per-head savings vs max-price (size-2 baseline)
        if (maxPerHead && perHead < maxPerHead) {
          totalSaved += (maxPerHead - perHead) * state.groupSize;
        }

        // Shipping savings, folded in once over free-shipping threshold
        var threshold = pricing.freeShippingThreshold || 6500;
        var shippingSaving = pricing.shippingValue || 799; // pence; default \u00a37.99
        if (totalCost >= threshold) {
          totalSaved += shippingSaving;
          includesShipping = true;
        }

        // No savings claim at a table of two: that is the baseline price, so
        // there is nothing yet to have saved.
        if (state.groupSize >= 3 && totalSaved > 0) {
          var msg = 'Save ' + formatPrice(totalSaved) + ' total vs ' +
                    formatPrice(maxPerHead) + '/head for 2';
          if (includesShipping) msg += ', incl. free delivery';
          savingsEl.innerHTML = '\u2714 ' + msg;
          savingsEl.style.display = '';
        } else {
          savingsEl.style.display = 'none';
        }
      }
    });

    updateImmersiveCard();
    updateRunningTotal();
    updateBoxCard();
    updateFeastCardImage();
    queueTierAlign();
  }

  // Immersive card price = Build Your Own per head x group size + the Hosting Pack
  // for that size (4 people: £90 + £20 = £110, £27.50 a head).
  function updateImmersiveCard() {
    if (!immCard) return;
    var size = state.groupSize;
    var perHead = pricing.feast[size];
    var pack = immPackPrice(size);
    var amountEl = immCard.querySelector('[data-tier-imm-amount]');
    var totalEl = immCard.querySelector('[data-tier-imm-total]');
    if (!perHead || !(pack > 0)) {
      if (amountEl) amountEl.innerHTML = '&mdash;';
      if (totalEl) totalEl.textContent = '';
      setTierFree(immCard, 0);
      return;
    }
    var total = perHead * size + pack;
    if (amountEl) amountEl.textContent = formatPrice(Math.round(total / size));
    setTierTotal(totalEl, total, size);
    setTierFree(immCard, total);
  }

  // ── Card alignment (desktop, three across) ──
  // When one card's title, descriptor or weight line wraps onto an extra line,
  // everything below it drops, so each of those rows takes the height of the
  // tallest, and the three foot boxes share one height so the totals, per-head
  // lines and buttons sit level. Off when the cards are not three across.
  var TIER_ROWS = ['.cuisine-tier-card__name', '.cuisine-tier-card__tagline', '.cuisine-tier-minweight', '.cuisine-box-carousel, .cuisine-tier-card__figure', '.cuisine-tier-buybox'];
  function alignTierCards() {
    var grid = document.querySelector('[data-cuisine-tiers]');
    if (!grid) return;
    var cards = grid.querySelectorAll('.cuisine-tier-card');
    var cols = 1;
    try { cols = getComputedStyle(grid).gridTemplateColumns.split(' ').length; } catch (e) {}
    TIER_ROWS.forEach(function(sel) {
      var els = [];
      cards.forEach(function(c) { var el = c.querySelector(sel); if (el) els.push(el); });
      els.forEach(function(el) { el.style.minHeight = ''; });
      if (cols < 3 || els.length < 2) return;
      var mx = 0;
      els.forEach(function(el) { mx = Math.max(mx, el.getBoundingClientRect().height); });
      els.forEach(function(el) { el.style.minHeight = Math.ceil(mx) + 'px'; });
    });
  }
  var tierAlignTimer = null;
  function queueTierAlign() { clearTimeout(tierAlignTimer); tierAlignTimer = setTimeout(alignTierCards, 120); }
  window.addEventListener('resize', queueTierAlign);
  (function watchTierGrid() {
    var grid = document.querySelector('[data-cuisine-tiers]');
    if (!grid) return;
    try { new MutationObserver(queueTierAlign).observe(grid, { subtree: true, childList: true, characterData: true }); } catch (e) {}
    try { if (window.ResizeObserver) new ResizeObserver(queueTierAlign).observe(grid); } catch (e) {}
    grid.querySelectorAll('img').forEach(function(im) { if (!im.complete) im.addEventListener('load', queueTierAlign, { once: true }); });
  })();

  // Per-size Build Your Own card image (Ben's mapping, 2026-09-01). The liquid
  // renders one <img data-feast-size-img="N"> per configured size; show the
  // largest configured size ≤ the current group size, clamping down to the
  // smallest when the group is below every configured size (can't happen with
  // a 2-image, but cheap to be safe) and up to the largest for 5+ cuisines
  // that only have images to 4.
  function updateFeastCardImage() {
    var imgs = document.querySelectorAll('[data-feast-size-img]');
    if (!imgs.length) return;
    var sizes = [];
    imgs.forEach(function(img) { sizes.push(parseInt(img.dataset.feastSizeImg, 10)); });
    sizes.sort(function(a, b) { return a - b; });
    var want = sizes[0];
    for (var i = 0; i < sizes.length; i++) {
      if (sizes[i] <= state.groupSize) want = sizes[i];
    }
    imgs.forEach(function(img) {
      img.hidden = parseInt(img.dataset.feastSizeImg, 10) !== want;
    });
  }

  // ══════════════════════════════════════════
  // ── BESTSELLERS BOX CARD ──
  // ══════════════════════════════════════════
  //
  // The box card is server-rendered from `box_dish` blocks. Three things have
  // to react to the group-size selector:
  //   1. Contents — a dish block may be scoped to certain sizes via
  //      `data-box-sizes` (the box composition changes as the table grows),
  //      so rows and carousel slides outside the current size are hidden.
  //   2. Weight — "X.XXkg total · XXXg per head" from the boxWeights map.
  //   3. Free delivery — the same threshold used everywhere else, shown on the
  //      card because the box is bought straight off it.

  var boxWeights = configData.boxWeights || {};

  // How many of a category the box includes at a given head count. Mirrors the
  // configurator's own rules exactly (getRequiredCount), because the box IS a
  // pre-made configurator selection — same dishes, same per-head maths.
  function boxRequiredCount(cat, size) {
    if (cat === 'sauce' && saucePerTwo) return Math.ceil(size / 2);
    return size;
  }

  // Compose the box for the current group size.
  //
  // Each row carries its ordinal position within its category. The box for N
  // people takes positions 1..K of each category, so a dish that appears at
  // both position 1 and position 5 shows once with "× 2" as soon as the group
  // reaches 5. Returns the composed picks so cart-add can reuse them.
  function composeBox(size) {
    var card = document.querySelector('[data-tier="box"]');
    if (!card) return [];

    var counts = {};   // dishId -> quantity in the box at this size
    var firstRow = {}; // dishId -> the row we show it on
    var picks = [];

    card.querySelectorAll('[data-box-dish]').forEach(function(row) {
      var cat = row.getAttribute('data-box-cat');
      var pos = parseInt(row.getAttribute('data-box-pos'), 10) || 0;
      var dishId = row.getAttribute('data-box-dish');
      var included = pos > 0 && pos <= boxRequiredCount(cat, size);

      if (!included) {
        row.hidden = true;
        return;
      }
      counts[dishId] = (counts[dishId] || 0) + 1;
      if (!firstRow[dishId]) {
        firstRow[dishId] = row;
        row.hidden = false;
      } else {
        // Duplicate of a dish already shown — fold it into the first row's
        // quantity rather than listing the same dish twice.
        row.hidden = true;
      }
      var gid = row.getAttribute('data-variant-id');
      if (gid) picks.push({ slot: cat + '_' + pos, variantId: gid });
    });

    // Stamp quantities onto the visible rows
    Object.keys(firstRow).forEach(function(dishId) {
      var badge = firstRow[dishId].querySelector('[data-box-dish-qty]');
      if (!badge) return;
      var qty = counts[dishId];
      if (qty > 1) {
        badge.textContent = ' \u00d7 ' + qty;
        badge.hidden = false;
      } else {
        badge.textContent = '';
        badge.hidden = true;
      }
    });

    // Carousel follows the composition — one slide per DISH actually in the
    // box, not one per block. A dish that occupies two positions (and so shows
    // as "× 2" in the list) still gets a single slide, otherwise the carousel
    // pages through the same photo twice.
    var slideSeen = {};
    card.querySelectorAll('[data-box-slide-dish]').forEach(function(slide) {
      var d = slide.getAttribute('data-box-slide-dish');
      var show = !!firstRow[d] && !slideSeen[d];
      if (show) slideSeen[d] = true;
      slide.hidden = !show;
    });

    return picks;
  }

  // The picks composing the box at the current size, reused at cart-add so the
  // Cart Transform Function expands the box into the same component lines a
  // hand-built feast would produce.
  var boxPicks = [];

  // Box stock check for a group size: first dish the composed box needs more of than is in stock, or null.
  function boxShortfall() {
    var need = {}, info = {};
    (boxPicks || []).forEach(function(pk) {
      var vid = String(String(pk.variantId).split('/').pop());
      need[vid] = (need[vid] || 0) + 1;
    });
    var rows = document.querySelectorAll('[data-tier="box"] [data-box-dish][data-stock]');
    for (var i = 0; i < rows.length; i++) {
      var vid = cardVariantNum(rows[i]);
      if (!need[vid] || info[vid]) continue;
      info[vid] = true;
      var st = stockOf(rows[i]);
      if (st !== null && need[vid] > st) return { stock: st, need: need[vid] };
    }
    return null;
  }
  function syncBoxAvailability(card) {
    var btn = card.querySelector('[data-select-tier="box"]');
    var extras = card.querySelector('[data-box-extras-cta]');
    var short = boxShortfall();
    var note = card.querySelector('[data-box-stock-note]');
    if (!note && short) {
      note = document.createElement('p');
      note.className = 'cuisine-stock-limit cuisine-box-stock-note';
      note.setAttribute('data-box-stock-note', '');
      note.setAttribute('role', 'status');
      (btn && btn.parentNode ? btn.parentNode : card).insertBefore(note, btn || null);
    }
    if (note) {
      note.hidden = !short;
      note.textContent = short ? 'Not available for ' + state.groupSize + ' this week. Try Build Your Own, or a smaller group.' : '';
    }
    [btn, extras].forEach(function(b) {
      if (!b) return;
      if (short) { b.disabled = true; b.setAttribute('data-stock-blocked', ''); }
      else if (b.hasAttribute('data-stock-blocked')) { b.disabled = false; b.removeAttribute('data-stock-blocked'); }
    });
  }

  function updateBoxCard() {
    var card = document.querySelector('[data-tier="box"]');
    if (!card) return;
    var size = state.groupSize;

    boxPicks = composeBox(size);
    boxCarouselGo(0);
    syncBoxAvailability(card);

    // Weight line. Prefer real per-size box weights when the owner has entered
    // them; otherwise fall back to the cuisine's guaranteed minimum per head,
    // which the box also meets — better an honest floor than a blank line.
    var grams = parseInt(boxWeights[String(size)], 10) || 0;
    var weightText;
    if (grams > 0) {
      weightText = 'Box total ' + (grams / 1000).toFixed(2) + 'kg \u00b7 ' + Math.round(grams / size) + 'g of food per head';
    } else if (configData && configData.minPerHead > 0) {
      weightText = 'Minimum box ' + ((configData.minPerHead * size) / 1000).toFixed(2) + 'kg \u00b7 ' + configData.minPerHead + 'g of food per head';
    } else {
      weightText = '';
    }
    card.querySelectorAll('[data-box-weight], [data-box-weight-foot]').forEach(function(el) {
      el.textContent = weightText;
      el.hidden = !weightText;
    });

    // Free-delivery progress for the box price at this size
    var wrap = card.querySelector('[data-box-freeship]');
    if (!wrap) return;
    var perHead = pricing.box && pricing.box[size];
    var threshold = pricing.freeShippingThreshold;
    if (!perHead || !threshold) { wrap.hidden = true; return; }
    var total = perHead * size;
    wrap.hidden = false;
    var pct = Math.min(100, Math.max(0, Math.round((total / threshold) * 100)));
    var fill = wrap.querySelector('[data-box-freeship-fill]');
    var text = wrap.querySelector('[data-box-freeship-text]');
    if (fill) fill.style.width = pct + '%';
    if (satDateSelected()) {
      wrap.classList.remove('is-unlocked');
      if (text) text.textContent = SAT_LINE;
    } else if (total >= threshold) {
      wrap.classList.add('is-unlocked');
      if (text) text.textContent = 'Free delivery included';
    } else {
      wrap.classList.remove('is-unlocked');
      if (text) text.textContent = 'Spend ' + formatPrice(threshold - total) + ' more for free delivery';
    }
  }

  // ── Box dish carousel ──
  // Progressive enhancement only: the slides are already in the DOM and
  // readable without JS; this adds paging.
  var boxCarousel = { index: 0 };

  function boxVisibleSlides() {
    var track = document.querySelector('[data-box-carousel-track]');
    if (!track) return [];
    return Array.prototype.filter.call(
      track.querySelectorAll('.cuisine-box-carousel__slide'),
      function(sl) { return !sl.hidden; }
    );
  }

  function boxCarouselGo(i) {
    var track = document.querySelector('[data-box-carousel-track]');
    var slides = boxVisibleSlides();
    if (!track || slides.length === 0) return;
    // Wrap around in both directions so the arrows never dead-end.
    boxCarousel.index = ((i % slides.length) + slides.length) % slides.length;
    track.style.transform = 'translateX(-' + (boxCarousel.index * 100) + '%)';
    boxCarouselRenderDots();
  }

  function boxCarouselRenderDots() {
    var dots = document.querySelector('[data-box-carousel-dots]');
    if (!dots) return;
    var count = boxVisibleSlides().length;
    if (dots.children.length !== count) {
      dots.textContent = '';
      for (var i = 0; i < count; i++) {
        var d = document.createElement('span');
        d.className = 'cuisine-box-carousel__dot';
        dots.appendChild(d);
      }
    }
    Array.prototype.forEach.call(dots.children, function(d, i) {
      d.classList.toggle('is-on', i === boxCarousel.index);
    });
  }

  (function initBoxCarousel() {
    var prev = document.querySelector('[data-box-carousel-prev]');
    var next = document.querySelector('[data-box-carousel-next]');
    if (prev) prev.addEventListener('click', function() { boxCarouselGo(boxCarousel.index - 1); });
    if (next) next.addEventListener('click', function() { boxCarouselGo(boxCarousel.index + 1); });
    // Hidden slides only settle once updateBoxCard has run for the initial
    // size, so leave the first paint to it.
  })();

  // ══════════════════════════════════════════
  // ── EXPANDABLE DISH DETAIL ──
  // ══════════════════════════════════════════
  //
  // One behaviour, two places (owner's explicit ask): the full dish
  // description reads identically whether the customer is looking at the fixed
  // Bestsellers Box or building their own. Delegated from the document so it
  // covers cards that are re-rendered or revealed later.
  //
  // On picker cards the toggle is its own button, deliberately NOT the whole
  // card — the card already owns the qty +/- controls and a card-level click
  // target would fight them.

  document.addEventListener('click', function(e) {
    var moreBtn = e.target.closest && e.target.closest('[data-dish-more]');
    if (moreBtn) {
      var info = moreBtn.parentNode;
      var full = info && info.querySelector('[data-dish-desc-full]');
      if (!full) return;
      var open = full.hidden;
      full.hidden = !open;
      moreBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      moreBtn.classList.toggle('is-open', open);
      var label = moreBtn.querySelector('.cuisine-picker-more__label');
      if (label) label.textContent = open ? 'Less' : 'Details';
      return;
    }
    var boxDish = e.target.closest && e.target.closest('.cuisine-box-dish.has-detail');
    if (boxDish) {
      var isOpen = boxDish.classList.toggle('is-open');
      boxDish.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  });

  // Keyboard parity for the box rows, which are role="button" containers
  // rather than real buttons (they wrap an image and several lines of text).
  document.addEventListener('keydown', function(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var row = e.target.closest && e.target.closest('.cuisine-box-dish.has-detail');
    if (!row) return;
    e.preventDefault();
    var isOpen = row.classList.toggle('is-open');
    row.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });

  function resetSelections() {
    state.selections = {};
    document.querySelectorAll('[data-qty-value]').forEach(function(el) {
      el.textContent = '0';
    });
    document.querySelectorAll('.cuisine-picker-card').forEach(function(card) {
      card.classList.remove('is-selected');
    });
  }

  // ══════════════════════════════════════════
  // ── PATH STEP PLAN ──
  // ══════════════════════════════════════════
  //
  // The two forks walk different numbers of steps through the same markup.
  // "Build Your Own" walks every step (mains → sides → … → extras → date).
  // The Bestsellers Box is already composed by us, so its dish-picker steps
  // are meaningless — it goes straight to the optional extras and the delivery
  // date. Rather than render a second set of step divs, the box path simply
  // walks a SUBSET of the same steps.
  //
  // Everything that used to reason about "step N of totalSteps" now reasons
  // about position within this plan, so the progress bar, the ticks, the
  // Back button and the footer CTA all stay correct on both paths.

  var allStepNumbers = Object.keys(stepTypeMap)
    .map(Number)
    .sort(function(a, b) { return a - b; });

  function activeSteps() {
    if (state.tier !== 'box') return allStepNumbers;
    // Ben (2026-09-01): extras folded into a panel on the date step was "not
    // what I asked for". The box path is extras → date again — a real step, so
    // the addon cards (Hosting Pack included) appear where he expects, and box
    // sessions fire the extras step_reached the rollup's box step list already
    // assumes. The main card CTA still fast-paths to the date step; the card's
    // "Add optional extras" button enters here. See the tier click handler.
    return allStepNumbers.filter(function(num) {
      return stepTypeMap[num] === 'addons' || stepTypeMap[num] === 'date';
    });
  }

  // Position of the current step within the active plan (0-based, clamped).
  function activeIndex() {
    var i = activeSteps().indexOf(state.currentStep);
    return i < 0 ? 0 : i;
  }

  function neighbourStep(delta) {
    var plan = activeSteps();
    var i = plan.indexOf(state.currentStep);
    if (i < 0) return null;
    var j = i + delta;
    return (j >= 0 && j < plan.length) ? plan[j] : null;
  }

  // Human label for the chosen path. Used in cart line properties, sold-out
  // copy and the confirmation UI, so the customer sees the same words we put
  // on the card they clicked.
  function tierLabel() {
    if (state.tier === 'box') return configData.boxName || 'Bestsellers Box';
    return 'The Feast';
  }

  var hasBoxPath = !!document.querySelector('[data-tier="box"]');

  // ══════════════════════════════════════════
  // ── PATH SELECTION ──
  // ══════════════════════════════════════════

  // Tier buttons are clickable IMMEDIATELY — the configurator opens on click
  // without waiting for the background product-data fetch. The step UI is
  // server-rendered (needs no fetched data to display), pricing comes from the
  // embedded pricingTable, and cart-add re-validates availability before adding
  // anything. Previously the buttons were disabled + "Loading…" until all ~30
  // /products/{handle}.js fetches resolved — which could be 10s+ on a slow or
  // uncached load, so an eager "Build Your Feast" click did nothing for ages.
  var tierBtns = document.querySelectorAll('[data-select-tier]');
  tierBtns.forEach(function(btn) {
    btn.dataset.originalText = btn.textContent;
  });

  tierBtns.forEach(function(btn) {
    btn.addEventListener('click', function() {
      if (this.disabled || this.classList.contains('is-unavailable')) return;
      var pickedTier = this.dataset.selectTier;
      var pickedImmersive = this.hasAttribute('data-select-immersive') && !!immPackKey;
      // Only enforce the sold-out guard once product data has loaded. Before
      // that we open optimistically (no wait); availability is re-checked when
      // the data arrives and again at cart-add, so nothing sold-out slips through.
      if (dataReady && !isBundleAvailable(pickedTier, state.groupSize)) {
        console.warn('[Configurator] Tier click ignored — ' + pickedTier + ' sold out for size', state.groupSize);
        return;
      }
      if (pickedImmersive && dataReady && !immPackAvailable(state.groupSize)) {
        console.warn('[Configurator] Immersive click ignored — Hosting Pack unavailable for size', state.groupSize);
        return;
      }
      // Coming back from the Immersive card to a plain card: take off the pack it added.
      if (!pickedImmersive && state.immersive && immPackKey && (state.addons.get(immPackKey) || 0) > 0 && !isRestoringState) {
        var packCardOff = document.querySelector('[data-addon="' + immPackKey + '"]');
        if (packCardOff) setAddonQty(packCardOff, 0); else state.addons.delete(immPackKey);
      }
      state.tier = pickedTier;
      state.immersive = pickedImmersive;
      // The Immersive Feast is the Build Your Own path with one Hosting Pack (the
      // variant for this group size) already in the extras. Not on a restore
      // replay: the saved extras are replayed separately.
      if (pickedImmersive && !isRestoringState) ensureImmersivePack();
      // Same restore-replay guard as size_selected — see comment there.
      if (!isRestoringState && window.WF && window.WF.event) {
        window.WF.event('tier_selected', { tier: pickedImmersive ? 'immersive' : pickedTier, size: state.groupSize, cuisine: state.cuisine || null });
        if (pickedImmersive) {
          window.WF.event('immersive_selected', { cuisine: configData.cuisineSlug || state.cuisine || null, size: state.groupSize, page: location.pathname });
        }
      }
      var titleEl = document.querySelector('[data-config-title]');
      if (titleEl) {
        titleEl.textContent = state.tier === 'box'
          ? (configData.boxName || 'Bestsellers Box')
          : configuratorTitle;
      }
      console.log('[Configurator] Tier selected:', state.tier);
      updateRunningTotal();
      // Box main CTA fast-paths to the date step (extras stays one Back away);
      // the card's "Add optional extras" button opens on the extras step.
      if (state.tier === 'box' && !extrasEntryRequested) {
        openConfigurator(typeStepMap['date'] || null);
      } else {
        openConfigurator();
      }
    });
  });

  // "Add optional extras" on the Bestsellers card (Ben, 2026-09-01). Delegates
  // to the main CTA so every guard (disabled, sold-out, data-not-ready) applies
  // identically — the flag just changes which step the flow opens on.
  var extrasEntryRequested = false;
  var boxExtrasCta = document.querySelector('[data-box-extras-cta]');
  if (boxExtrasCta) {
    boxExtrasCta.addEventListener('click', function() {
      var mainCta = document.querySelector('[data-tier="box"] [data-select-tier]');
      if (!mainCta || mainCta.disabled || mainCta.classList.contains('is-unavailable')) return;
      extrasEntryRequested = true;
      try { mainCta.click(); } finally { extrasEntryRequested = false; }
    });
  }

  var changeBtn = document.querySelector('[data-config-change]');
  if (changeBtn) {
    changeBtn.addEventListener('click', function() {
      closeConfigurator();
      // Ben (2026-09-01): closing used to strand the customer at the top of
      // the page. "Change tier" means "back to the choice", so land them on
      // the fork itself once the page chrome has unsealed.
      var fork = document.querySelector('[data-cuisine-tiers]');
      if (fork) scrollToBuilder(fork);
    });
  }

  // Jump (never glide) to the top of the builder. Why not scrollIntoView smooth:
  // opening the builder hides every other section (body.wf-config-active), so the
  // page collapses from ~19,000px to ~2,000px in the same instant. A smooth scroll
  // aimed at the old position then lands 1,400px off in a single frame and the page
  // visibly lurches; on each "Continue" the glide also fought the step swapping
  // underneath it. Ben (15 Oct 2026): "Immersive Feast builder jerky ... jarring".
  // An instant jump after the layout change has nothing to animate, so nothing jars,
  // and prefers-reduced-motion is respected for free. Reads layout AFTER the class
  // change, so the target is the collapsed position.
  function scrollToBuilder(el) {
    el = el || configuratorEl;
    if (!el) return;
    var go = function () {
      var top = el.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop || 0);
      try { window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'instant' }); }
      catch (e) { window.scrollTo(0, Math.max(0, top)); }
    };
    go();
    // Once more after images/fonts settle, in case the first read was mid-layout.
    window.requestAnimationFrame(function () { window.requestAnimationFrame(go); });
  }

  function openConfigurator(startStep) {
    if (configuratorEl) {
      configuratorEl.classList.add('is-open');
      // Mark on body so third-party popup overlays (Klaviyo, Bloop) — and, as
      // of the Phase-1 "sealed flow", the whole site chrome — can hide via CSS
      // while the customer is mid-build. Once building starts every visible nav
      // link is an exit, and the Clarity recordings showed people taking them;
      // sealing turns the builder into a focused, checkout-style flow.
      document.body.classList.add('wf-config-active');
      // Tag THIS builder's section so the sealing CSS can hide every OTHER page
      // section (delivery map, reviews, videos, FAQ…) without a :has() query.
      var hostSection = configuratorEl.closest('.shopify-section');
      if (hostSection) hostSection.classList.add('wf-config-host-section');
      // Re-derive the step plan for the path just chosen BEFORE the first
      // render, so the progress bar and its ticks are drawn against the right
      // step count (the box walks fewer steps than Build Your Own).
      var plan = activeSteps();
      state.totalSteps = plan.length;
      renderProgressTicks();
      // A caller may open on a specific step of the plan (the box main CTA
      // fast-paths to the date step); anything not in the plan falls back to
      // the plan's first step.
      state.currentStep = (startStep && plan.indexOf(startStep) !== -1 ? startStep : plan[0]) || 1;
      showStep(state.currentStep);
      updateProgressDots();
      // Suppress the scroll when we're replaying a saved state on page
      // load — otherwise the synthetic tier click yanks the page down
      // to the configurator before the customer has even seen the hero.
      if (!isRestoringState) {
        scrollToBuilder();
      }
    }
  }

  function closeConfigurator() {
    if (configuratorEl) {
      configuratorEl.classList.remove('is-open');
      document.body.classList.remove('wf-config-active');
      var hostSection = configuratorEl.closest('.shopify-section');
      if (hostSection) hostSection.classList.remove('wf-config-host-section');
      state.tier = null;
      state.immersive = false;
      state.selectedDate = null;
      state.addons = new Map();
      resetSelections();
      updatePricing(); // back to the Friday delivery wording
      // Reset addon qty selectors back to 0.
      document.querySelectorAll('.cuisine-addon-card.is-added').forEach(function(card) {
        card.classList.remove('is-added');
        var num = card.querySelector('[data-addon-qty-value]');
        if (num) num.textContent = '0';
      });
      // Reset date display
      var dateEl = document.querySelector('[data-date-selected]');
      if (dateEl) dateEl.textContent = saturdayEnabled ? 'Select a date' : 'Select a Friday';
      var dispatchEl = document.querySelector('[data-date-dispatch]');
      if (dispatchEl) dispatchEl.textContent = '—';
      var cutoffEl = document.querySelector('[data-date-cutoff]');
      if (cutoffEl) cutoffEl.textContent = '—';
    }
  }

  // ══════════════════════════════════════════
  // ── STEP NAVIGATION ──
  // ══════════════════════════════════════════

  // Date step only: show the chosen delivery date in the sticky footer
  // ("Fri 23 Oct"), so on a short phone the choice stays visible whatever is
  // scrolled into view. Purely presentational: it reads state, never writes it.
  // The element is shown by CSS on short phone screens only.
  function syncFooterDate() {
    var foot = document.querySelector('.cuisine-config-footer');
    var el = document.querySelector('[data-config-date]');
    var onDate = stepTypeMap[state.currentStep] === 'date';
    if (foot) foot.classList.toggle('is-date-step', !!onDate);
    if (!el) return;
    var parts = (state.selectedDate || '').split('-');
    if (!onDate || parts.length !== 3) { el.hidden = true; el.textContent = ''; return; }
    var d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    var sd = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    var sm = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    el.textContent = 'Delivery ' + sd[d.getDay()] + ' ' + d.getDate() + ' ' + sm[d.getMonth()];
    el.hidden = false;
  }

  function showStep(n) {
    document.querySelectorAll('[data-config-step]').forEach(function(step) {
      step.classList.toggle('is-visible', parseInt(step.dataset.configStep, 10) === n);
    });
    state.currentStep = n;
    updateProgressDots();
    updateProgressBar();
    updateFooterButton();
    syncFooterDate();
    // ── Per-step funnel telemetry ──
    // Fire on entry to every step so the rollup can show how many sessions
    // reach the extras step vs the delivery-date step vs add-to-cart — the
    // breakdown the owner needs to judge Phase 1 (previously the funnel jumped
    // straight from dish_picked → date_selected, hiding the extras step). The
    // rollup counts each session's FURTHEST step, so re-entering a step via
    // Back doesn't inflate anything. Guarded against the restore-replay so a
    // returning customer's rebuilt state doesn't double-count.
    if (!isRestoringState && window.WF && window.WF.event) {
      window.WF.event('step_reached', {
        step: n,
        type: stepTypeMap[n] || null,
        total: state.totalSteps || totalSteps,
        path: eventTier(),
        cuisine: state.cuisine || null
      });
    }

    // ── Date-step timing + lead-time capture ──
    // Entering the date step starts the dwell clock and records how far away
    // the soonest available Friday actually is, which is the single number that
    // separates "too far away" from the other explanations.
    if (stepTypeMap[n] === 'date') {
      dateStepEnteredAt = Date.now();
      dateStepSawMore = false;
      dateStepReported = false;
      if (!isRestoringState && window.WF && window.WF.event) {
        window.WF.event('date_step_viewed', dateStepContext());
      }
      // Arm the soonest Friday so Add to Cart lights up on arrival (Ben,
      // 2026-09-01). Emits date_preselected, never date_selected — see
      // autoPreselectDate. Runs after the viewed event so event order in a
      // session journey stays viewed → preselected → (selected | abandon).
      autoPreselectDate();
    } else if (dateStepEnteredAt) {
      // Stepped backwards out of the date step without picking — a different
      // signal from leaving the site, so record it as its own reason.
      reportDateStepAbandon('went_back');
      dateStepEnteredAt = 0;
    }
  }

  // Owner's "gamified" progress bar (WorldFeast-ProgressBar-Final mockup):
  // a single teal→coral fill + "Step X of Y" + honest proximity message,
  // shown at the top of every step. Replaces the numbered dots as the primary
  // progress cue (dots are hidden via CSS but left in the DOM).
  function updateProgressBar() {
    var plan = activeSteps();
    var total = plan.length || state.totalSteps || totalSteps;
    var n = state.currentStep;
    var pos = activeIndex();
    var fill = document.querySelector('[data-progressbar-fill]');
    var msgEl = document.querySelector('[data-progressbar-msg]');
    var countEl = document.querySelector('[data-progressbar-count]');
    if (fill) {
      // Within-step progress. The fill advances as you add your included items
      // in a step (and lands exactly on the step mark once the step's quota is
      // met), then continues from there on the next step — so the middle of the
      // flow visibly moves as you build instead of freezing between the 4-5 big
      // step jumps. completedSteps = steps behind you; frac = how far through the
      // current step you are (0.25 credit for arriving → 1.0 when the step's done).
      var type = stepTypeMap[n];
      var frac;
      if (type === 'date') {
        frac = state.selectedDate ? 1 : 0.25;
      } else if (type === 'addons' || !type) {
        frac = 1; // optional/unknown step: full credit on arrival, never stalls
      } else {
        var required = getRequiredCount(type) || 0;
        var got = getTotalForType(type) || 0;
        var raw = required > 0 ? Math.min(1, got / required) : 1;
        frac = 0.25 + 0.75 * raw;
      }
      var pct = (pos + frac) / total;
      fill.style.transform = 'scaleX(' + Math.max(0.02, Math.min(1, pct)).toFixed(4) + ')';
    }
    if (countEl) countEl.textContent = 'Step ' + (pos + 1) + ' of ' + total;
    // Show the current step's heading here ("Choose your mains", "Pick your
    // delivery date", …) rather than a generic "Let's begin / Nearly there"
    // message — the teal→coral fill already signals proximity, and this removes
    // the duplicate heading + the dead gap under the bar. The in-body h3 is
    // hidden via CSS.
    if (msgEl) {
      var headingEl = document.querySelector('.cuisine-config-step.is-visible h3');
      if (headingEl) msgEl.textContent = headingEl.textContent.trim();
    }
  }

  // Draw the step dividers on the progress track: one gap at each step boundary
  // so the whole journey — and how few steps remain — reads at a glance. Based
  // on the real step count, so it stays correct if the flow length changes.
  function renderProgressTicks() {
    var track = document.querySelector('.cuisine-config-progressbar__track');
    if (!track) return;
    var total = activeSteps().length || state.totalSteps || totalSteps;
    track.querySelectorAll('.cuisine-config-progressbar__tick').forEach(function(t) { t.remove(); });
    for (var i = 1; i < total; i++) {
      var tick = document.createElement('span');
      tick.className = 'cuisine-config-progressbar__tick';
      tick.style.left = (i / total * 100).toFixed(4) + '%';
      track.appendChild(tick);
    }
  }

  function updateProgressDots() {
    document.querySelectorAll('[data-step-dot]').forEach(function(dot) {
      var step = parseInt(dot.dataset.stepDot, 10);
      dot.classList.remove('is-active', 'is-complete');
      if (step < state.currentStep) {
        dot.classList.add('is-complete');
        dot.textContent = '✓';
      } else if (step === state.currentStep) {
        dot.classList.add('is-active');
        dot.textContent = step;
      } else {
        dot.textContent = step;
      }
    });
  }

  document.querySelectorAll('[data-step-back]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var prev = neighbourStep(-1);
      if (prev !== null) showStep(prev);
    });
  });

  // ══════════════════════════════════════════
  // ── QUANTITY CONTROLS ──
  // ══════════════════════════════════════════

  // Picker dish prices — pence — read from data-price set in Liquid.
  // Used to price extras beyond the included minimum.
  var pickerPrices = {};
  document.querySelectorAll('[data-picker][data-dish][data-price]').forEach(function(card) {
    pickerPrices[card.dataset.picker + ':' + card.dataset.dish] = parseInt(card.dataset.price, 10) || 0;
  });


  // ══════════════════════════════════════════
  // ── STOCK LIMITS (Ben 15 Oct 2026) ──
  // "Dish stock levels in Shopify are real limits." Liquid stamps data-stock on
  // a card/row only when its variant is tracked by Shopify AND denies overselling
  // (so the number is a hard ceiling); everything else stays unlimited. All the
  // places that can draw the same variant are summed: the Build Your Own / Immersive
  // picks (included and extra), the side dishes offered on the extras step, and the
  // Bestsellers Box's composed dishes.
  // ══════════════════════════════════════════
  function stockOf(el) {
    if (!el || el.getAttribute('data-stock') === null) return null;
    var n = parseInt(el.getAttribute('data-stock'), 10);
    return isNaN(n) ? null : n;
  }
  function cardVariantNum(el) {
    var v = el.getAttribute('data-variant-id-numeric');
    if (v) return String(v);
    var gid = el.getAttribute('data-variant-id');
    return gid ? String(gid.split('/').pop()) : '';
  }
  // How many of one variant the current build would draw, optionally ignoring one card's own count.
  function variantDemand(vid) {
    if (!vid) return 0;
    var total = 0;
    if (state.tier === 'box') {
      (boxPicks || []).forEach(function(pk) { if (String(String(pk.variantId).split('/').pop()) === vid) total++; });
    } else {
      document.querySelectorAll('.cuisine-picker-card[data-picker][data-dish]').forEach(function(c) {
        if (cardVariantNum(c) !== vid) return;
        var q = (state.selections[c.dataset.picker] || {})[c.dataset.dish];
        total += parseInt(q, 10) || 0;
      });
    }
    document.querySelectorAll('.cuisine-addon-card[data-addon][data-variant-id-numeric]').forEach(function(c) {
      if (cardVariantNum(c) !== vid) return;
      total += state.addons.get(c.dataset.addon) || 0;
    });
    return total;
  }
  // Show/hide the "Only N left" and "That's all we have" notes, and dim a + that can go no further.
  function updateStockNotes() {
    document.querySelectorAll('.cuisine-picker-card[data-stock], .cuisine-addon-card[data-stock]').forEach(function(c) {
      var stock = stockOf(c);
      var vid = cardVariantNum(c);
      var demand = variantDemand(vid);
      var atLimit = stock !== null && demand >= stock;
      var lim = c.querySelector('[data-stock-limit]');
      if (lim) lim.hidden = !(atLimit && stock > 0);
      // "Only N left" gives way to the at-limit message so the card never says both.
      var left = c.querySelector('[data-stock-note]');
      if (left) {
        left.hidden = !(stock >= 0 && stock < 10) || (atLimit && !!lim && stock > 0);
        left.textContent = stock === 0 ? 'Sold out' : 'Only ' + stock + ' left';
      }
      var plus = c.querySelector('[data-qty-plus], [data-addon-qty-plus]');
      if (plus) {
        plus.classList.toggle('is-at-limit', atLimit);
        plus.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
      }
    });
  }
  // First variant whose demand exceeds its stock, as { label, stock, demand }; null when all fit.
  function findStockShortfall() {
    var seen = {};
    var els = document.querySelectorAll('.cuisine-picker-card[data-stock], .cuisine-addon-card[data-stock], [data-tier="box"] [data-box-dish][data-stock]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var vid = cardVariantNum(el);
      if (!vid || seen[vid]) continue;
      seen[vid] = true;
      var stock = stockOf(el);
      var demand = variantDemand(vid);
      if (stock !== null && demand > stock) {
        var nameEl = el.querySelector('.name, h4, .cuisine-box-dish__name span');
        var label = nameEl ? (nameEl.childNodes[0] ? String(nameEl.childNodes[0].textContent) : nameEl.textContent).trim() : 'one of the dishes';
        return { label: label, stock: stock, demand: demand };
      }
    }
    return null;
  }

  document.querySelectorAll('[data-qty-plus]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var card = this.closest('.cuisine-picker-card');
      if (card.classList.contains('is-sold-out') || card.dataset.soldOut === 'true') return;
      var type = card.dataset.picker;
      var dish = card.dataset.dish;
      var valEl = card.querySelector('[data-qty-value]');
      var current = parseInt(valEl.textContent, 10) || 0;

      // Stock ceiling: a tracked dish that denies overselling stops at its stock.
      var plusStock = stockOf(card);
      if (plusStock !== null && variantDemand(cardVariantNum(card)) >= plusStock) {
        var plusLim = card.querySelector('[data-stock-limit]');
        if (plusLim && plusStock > 0) plusLim.hidden = false;
        return;
      }

      // Otherwise no upper limit: the included count is a minimum; any additional picks
      // become à la carte extras (priced separately at cart-add time).
      valEl.textContent = current + 1;
      setSelection(type, dish, current + 1);
      // Restore-replay guard: dish picks are replayed via plus.click() during
      // restorePersistedState() — would emit one dish_picked per restored qty.
      if (!isRestoringState && window.WF && window.WF.event) window.WF.event('dish_picked', { type: type, dish: dish, count: current + 1, direction: 'inc' });
      card.classList.add('is-selected');
      updateExtraBadges(type);
      validateStep(getStepForType(type));
      updateRunningTotal();
    });
  });

  document.querySelectorAll('[data-qty-minus]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var card = this.closest('.cuisine-picker-card');
      if (card.classList.contains('is-sold-out') || card.dataset.soldOut === 'true') return;
      var type = card.dataset.picker;
      var dish = card.dataset.dish;
      var valEl = card.querySelector('[data-qty-value]');
      var current = parseInt(valEl.textContent, 10) || 0;

      if (current > 0) {
        valEl.textContent = current - 1;
        setSelection(type, dish, current - 1);
        card.classList.toggle('is-selected', current - 1 > 0);
        updateExtraBadges(type);
        validateStep(getStepForType(type));
        updateRunningTotal();
      }
    });
  });

  function setSelection(type, dish, qty) {
    if (!state.selections[type]) state.selections[type] = {};
    state.selections[type][dish] = qty;
    if (qty <= 0) delete state.selections[type][dish];
  }

  // Force a pick down to zero AND update the card UI to match — used when we
  // need to drop a pick the customer didn't actively unselect (e.g. dish has
  // just sold out, restored stale state). Mirrors what the [data-qty-minus]
  // handler does so the counter, classes, and totals all stay consistent.
  function dropPickAndRefreshCard(type, dish) {
    var card = document.querySelector('[data-picker="' + type + '"][data-dish="' + dish + '"]');
    if (card) {
      var valEl = card.querySelector('[data-qty-value]');
      if (valEl) valEl.textContent = '0';
      card.classList.remove('is-selected');
    }
    setSelection(type, dish, 0);
    if (typeof updateExtraBadges === 'function') updateExtraBadges(type);
    var stepFn = typeof getStepForType === 'function' ? getStepForType : null;
    if (stepFn && typeof validateStep === 'function') validateStep(stepFn(type));
  }

  // Walk current state.selections and return picks whose card has flipped to
  // sold-out (the card class is updated by refreshPickerDishAvailability —
  // run that first if you want fresh truth). Each item: { type, dish, label }.
  function findSoldOutPicks() {
    var out = [];
    Object.keys(state.selections || {}).forEach(function(type) {
      var typeSel = state.selections[type] || {};
      Object.keys(typeSel).forEach(function(dish) {
        var qty = parseInt(typeSel[dish], 10) || 0;
        if (qty <= 0) return;
        var card = document.querySelector('[data-picker="' + type + '"][data-dish="' + dish + '"]');
        if (!card) {
          out.push({ type: type, dish: dish, label: dish });
          return;
        }
        if (card.classList.contains('is-sold-out') || card.dataset.soldOut === 'true') {
          var nameEl = card.querySelector('.cuisine-picker-name, .cuisine-picker-card__name, h4, h3');
          out.push({ type: type, dish: dish, label: nameEl ? nameEl.textContent.trim() : dish });
        }
      });
    });
    return out;
  }

  function getTotalForType(type) {
    var total = 0;
    if (state.selections[type]) {
      Object.values(state.selections[type]).forEach(function(v) { total += v; });
    }
    return total;
  }

  function getRequiredCount(type) {
    if (type === 'sauce' && saucePerTwo) return Math.ceil(state.groupSize / 2);
    return state.groupSize;
  }

  // Allocate a customer's selections in a category into "included in bundle"
  // vs "à la carte extras". Walks selections in insertion order (most-recent
  // dishes overflow first) so the bundle's _wf_picks always sums to the
  // required count and any overflow is priced separately.
  function getIncludedAndExtras(type) {
    var required = getRequiredCount(type);
    var result = { included: {}, extras: {}, includedCount: 0, extrasCount: 0 };
    var includedSoFar = 0;
    var selections = state.selections[type] || {};
    Object.keys(selections).forEach(function(dish) {
      var qty = selections[dish] || 0;
      if (qty <= 0) return;
      var capacity = Math.max(0, required - includedSoFar);
      var includeFromThis = Math.min(qty, capacity);
      var extraFromThis = qty - includeFromThis;
      if (includeFromThis > 0) result.included[dish] = includeFromThis;
      if (extraFromThis > 0) result.extras[dish] = extraFromThis;
      includedSoFar += includeFromThis;
      result.includedCount += includeFromThis;
      result.extrasCount += extraFromThis;
    });
    return result;
  }

  // Sum of à la carte extras for a category in pence.
  function computeExtrasTotalForType(type) {
    var split = getIncludedAndExtras(type);
    var total = 0;
    Object.keys(split.extras).forEach(function(dish) {
      var price = pickerPrices[type + ':' + dish] || 0;
      total += price * split.extras[dish];
    });
    return total;
  }

  // Show the "+£X each extra" badge on every card in a category once
  // the customer has hit the included minimum. Above the minimum, every
  // further click costs money — the badge makes that visible up-front.
  function updateExtraBadges(type) {
    var total = getTotalForType(type);
    var required = getRequiredCount(type);
    var atOrOverMin = total >= required;
    document.querySelectorAll('[data-picker="' + type + '"]').forEach(function(card) {
      var badge = card.querySelector('[data-extra-badge]');
      if (!badge) return;
      var price = parseInt(card.dataset.price, 10) || 0;
      if (atOrOverMin && price > 0) {
        badge.removeAttribute('hidden');
      } else {
        badge.setAttribute('hidden', '');
      }
    });
  }

  function getStepForType(type) {
    return typeStepMap[type] || 1;
  }

  function validateStep(stepNum) {
    var validationEl = document.querySelector('[data-step-validation="' + stepNum + '"]');
    if (!validationEl) return;

    var type = stepTypeMap[stepNum];
    if (!type || type === 'addons' || type === 'date') return;

    var total = getTotalForType(type);
    var required = getRequiredCount(type);
    var isComplete = total >= required;

    if (isComplete) {
      var extras = total - required;
      if (extras > 0) {
        var extrasPence = computeExtrasTotalForType(type);
        validationEl.textContent = '✓ ' + required + ' included + ' + extras + ' extra' + (extras > 1 ? 's' : '') + ' (+' + formatPrice(extrasPence) + ')';
      } else {
        validationEl.textContent = '✓ ' + required + ' of ' + required + ' ' + type + 's included — add more if you please';
      }
      validationEl.classList.remove('cuisine-step-validation--warning');
    } else {
      var remaining = required - total;
      validationEl.textContent = 'Choose at least ' + remaining + ' more ' + type + (remaining > 1 ? 's' : '') + ' to continue';
      validationEl.classList.add('cuisine-step-validation--warning');
    }

    updateFooterButton();
    updateProgressBar(); // nudge the fill as included items are added/removed
  }

  function validateAllSteps() {
    pickerTypes.forEach(function(type) {
      validateStep(typeStepMap[type]);
      updateExtraBadges(type);
    });
  }

  // ══════════════════════════════════════════
  // ── RUNNING TOTAL ──
  // ══════════════════════════════════════════
  // Note: Express AM delivery is a Shopify shipping rate, not a product —
  // chosen by the customer at checkout. No express uplift appears in the
  // configurator total because shipping rates aren't visible to storefront JS.

  // Compute the customer's current grand total in pence. Pulled out of
  // updateRunningTotal so the addtocart_success event can include it
  // without re-running the DOM-write side effects.
  function computeGrandTotalPence() {
    if (!state.tier) return 0;
    var perHead = pricing[state.tier][state.groupSize] || 0;
    var baseTotal = perHead * state.groupSize;
    var pickerExtrasTotal = 0;
    pickerTypes.forEach(function(t) {
      pickerExtrasTotal += computeExtrasTotalForType(t);
    });
    var addonsTotal = 0;
    state.addons.forEach(function(qty, addon) {
      if (addonPrices[addon]) addonsTotal += addonPrices[addon] * qty;
    });
    return baseTotal + pickerExtrasTotal + addonsTotal;
  }

  // ══════════════════════════════════════════
  // ── FOOD WEIGHT DISPLAY (PROTOTYPE) ──
  // ══════════════════════════════════════════
  // Shows each dish's food weight on its card, plus a running "total food"
  // gram counter in the footer. The owner's ask: portion sizes must map to a
  // real amount of food, including the accompaniments that come automatically
  // (rice, sauces, pickles) which the customer never selects.
  //
  // Real data source (forward-compatible): product metafield
  //   custom.food_components  → JSON like
  //   {"total_g":600,"components":[
  //      {"label":"Chicken curry","g":400,"main":true},
  //      {"label":"Basmati rice","g":200,"included":true}]}
  // The card reads that via data-weight-json. Where the metafield is absent
  // (i.e. right now, before we populate it) we fall back to category-based
  // DEMO_WEIGHTS so the concept is fully visible. Delete DEMO_WEIGHTS once the
  // real metafields land — nothing else changes.
  var DEMO_WEIGHTS = {
    main:  { mainG: 400, includedG: 200 }, // 600g total incl. accompaniments
    side:  { mainG: 220 },
    sauce: { mainG: 60 },
    _default: { mainG: 250 }
  };
  // Demo accompaniment wording per cuisine so the placeholder reads sensibly on
  // each page (BBQ brisket must not say "+200g rice"). Real per-dish data from
  // the metafield replaces this entirely once populated.
  var DEMO_ACCOMP = {
    southindian: 'rice, sauces & accompaniments',
    bbq:         'cornbread, slaw & pickles',
    vietnamese:  'rice, herbs & pickles'
  };
  var _demoAccompCache = null;
  function demoAccompLabel() {
    if (_demoAccompCache !== null) return _demoAccompCache;
    var label = 'sides & accompaniments';
    try {
      var c = JSON.parse(document.querySelector('[data-cuisine-products]').textContent).bundleCuisine;
      if (c && DEMO_ACCOMP[c]) label = DEMO_ACCOMP[c];
    } catch (e) { /* keep default */ }
    return (_demoAccompCache = label);
  }
  var weightsRendered = false;
  // §6/§7 minimum per-head weight — set from the lightest valid build so it's
  // always true. Real value comes from the section's min_per_head_g setting
  // (BBQ 765, South Indian 1140, Vietnamese 935); 650 is the last-resort default.
  var MIN_PER_HEAD_G = (configData && configData.minPerHead) || 650;

  function fmtGrams(g) {
    if (g >= 1000) return (g / 1000).toFixed(g % 1000 === 0 ? 0 : 1) + 'kg';
    return g + 'g';
  }

  // Pull the dish title only (strip trailing badge <span>s) for the breakdown.
  function cleanDishName(card) {
    var nameEl = card.querySelector('.cuisine-picker-info .name');
    if (!nameEl) return 'this dish';
    var t = '';
    nameEl.childNodes.forEach(function(n) { if (n.nodeType === 3) t += n.textContent; });
    t = t.trim();
    return t || 'this dish';
  }

  // Real per-dish weights, keyed by dish_id, from the section's dish_weights_json.
  // Compact shape per dish: { t: totalGrams, c: [[label,grams],…], p: portion, s2: servesTwo }
  var DISH_WEIGHTS = (configData && configData.dishWeights) || {};

  // Return {total_g, components:[{label,g}], portion, serves2} for a card — real
  // weights from the section map (by dish_id) first, then legacy metafield, then DEMO.
  function weightForCard(card) {
    // Picker cards key by data-dish; add-on (extras) cards key by data-addon —
    // both resolve against the same DISH_WEIGHTS map.
    var id = card.getAttribute('data-dish') || card.getAttribute('data-addon');
    var w = id && DISH_WEIGHTS[id];
    if (w && w.t) {
      return {
        total_g: w.t,
        components: (w.c || []).map(function(x) { return { label: x[0], g: x[1] }; }),
        portion: w.p || null,
        serves2: !!w.s2
      };
    }
    var raw = card.getAttribute('data-weight-json');
    if (raw) {
      try {
        var d = JSON.parse(raw);
        if (d && d.total_g && Array.isArray(d.components)) return d;
      } catch (e) { /* fall through to demo */ }
    }
    var cat = card.getAttribute('data-picker') || '_default';
    var demo = DEMO_WEIGHTS[cat] || DEMO_WEIGHTS._default;
    var comps = [{ label: cleanDishName(card), g: demo.mainG, main: true }];
    if (demo.includedG) comps.push({ label: demoAccompLabel(), g: demo.includedG, included: true });
    var total = comps.reduce(function(sum, c) { return sum + c.g; }, 0);
    return { total_g: total, components: comps };
  }

  function paintWeight(el, w) {
    // Headline: a portion label ("2 thighs") or "Serves 2" prefix, then the
    // all-in weight. Below it, the components listed SEPARATELY (owner wants
    // the rice/sauce/etc. shown, not folded into one number) — only when the
    // dish genuinely has more than one part, so single-item sides stay clean.
    var head = w.serves2 ? 'Serves 2 · ' : (w.portion ? w.portion + ' · ' : '');
    var html = '<span class="wf-wt-total">' + head + fmtGrams(w.total_g) + '</span>';
    var comps = w.components || [];
    if (comps.length > 1) {
      html += '<span class="wf-wt-parts">' + comps.map(function(c) {
        return fmtGrams(c.g) + ' ' + c.label.toLowerCase();
      }).join(' · ') + '</span>';
    }
    el.innerHTML = html;
  }

  function renderCardWeights() {
    document.querySelectorAll('.cuisine-picker-card').forEach(function(card) {
      var el = card.querySelector('[data-picker-weight]');
      if (!el) return;
      var w = weightForCard(card);
      card.dataset.weightTotal = w.total_g;
      paintWeight(el, w);
    });
    // Add-on (extras) cards: only the FOOD extras carry a weight in the map;
    // drinks (beer/wine/cocktail) have no entry, so they show nothing and add 0.
    document.querySelectorAll('.cuisine-addon-card').forEach(function(card) {
      var el = card.querySelector('[data-addon-weight]');
      var id = card.getAttribute('data-addon');
      var wd = id && DISH_WEIGHTS[id];
      if (!wd || !wd.t) { card.dataset.weightTotal = 0; return; }
      card.dataset.weightTotal = wd.t;
      if (el) paintWeight(el, {
        total_g: wd.t,
        components: (wd.c || []).map(function(x) { return { label: x[0], g: x[1] }; }),
        portion: wd.p || null, serves2: !!wd.s2
      });
    });
    // Bestsellers Box rows: the same real weights, so the always-open list says how much of each dish.
    document.querySelectorAll('[data-box-dish-amount]').forEach(function(el) {
      var row = el.closest('[data-box-dish]');
      var wd = row && DISH_WEIGHTS[row.getAttribute('data-box-dish')];
      if (!wd || !wd.t) { el.textContent = ''; return; }
      el.textContent = (wd.s2 ? 'Serves 2 \u00b7 ' : (wd.p ? wd.p + ' \u00b7 ' : '')) + fmtGrams(wd.t);
    });
    weightsRendered = true;
  }

  // Sum food weight across every selected dish × its quantity — mains/sides/etc.
  // AND any weighted extras (add-ons), so the running total reflects the whole
  // feast. Add-ons contribute their food weight to the total and, divided by
  // head count, to the per-head figure (owner: extras must move both numbers).
  function updateWeightTotal() {
    var totalG = 0;
    document.querySelectorAll('.cuisine-picker-card, .cuisine-addon-card').forEach(function(card) {
      if (card.getAttribute('data-sold-out') === 'true') return;
      var valEl = card.querySelector('[data-qty-value], [data-addon-qty-value]');
      var qty = valEl ? (parseInt(valEl.textContent, 10) || 0) : 0;
      var per = parseInt(card.dataset.weightTotal, 10) || 0;
      totalG += qty * per;
    });
    var el = document.querySelector('[data-config-weight]');
    if (!el) return;
    // Deck §7: show the PER-HEAD average (total food ÷ head count), benchmarked
    // against the minimum — NOT one person's dishes, and NOT the gross total.
    // Recomputes live whenever dishes OR head-count change.
    var heads = (state && state.groupSize) ? state.groupSize : 0;
    if (totalG > 0 && heads > 0) {
      var perHead = Math.round((totalG / heads) / 10) * 10; // ~ nearest 10g
      // "well over" is reserved for a MATERIAL margin (≥10% above the minimum) —
      // owner: a feast 10g over shouldn't claim "well over", that reads as hype.
      var rel;
      if (perHead >= Math.round(MIN_PER_HEAD_G * 1.1)) rel = 'well over the ' + MIN_PER_HEAD_G + 'g minimum';
      else if (perHead >= MIN_PER_HEAD_G) rel = 'over the ' + MIN_PER_HEAD_G + 'g minimum';
      else rel = 'on the way to the ' + MIN_PER_HEAD_G + 'g minimum';
      el.hidden = false;
      // Owner: lead with the total feast weight (kg reads bigger/better), then
      // per-head. fmtGrams converts ≥1kg to "X.Xkg".
      el.textContent = 'Your feast: ~' + fmtGrams(totalG) + ' total · ~' + perHead + 'g of food per head — ' + rel;
    } else {
      el.hidden = true;
    }
  }

  // Paint per-dish weights immediately at load. The picker cards are
  // server-rendered and this reads no product data, so it must NOT wait for the
  // async price fetch / first updateRunningTotal() — otherwise cards flash
  // weightless on slow loads (and never paint at all if a tier is never picked).
  renderCardWeights();

  function updateRunningTotal() {
    updateStockNotes();
    persistState();
    if (!weightsRendered) renderCardWeights();
    updateWeightTotal();
    if (!state.tier) return;

    var perHead = pricing[state.tier][state.groupSize] || 0;
    var baseTotal = perHead * state.groupSize;

    // À la carte picker extras (mains/sides/etc. beyond the included minimum)
    var pickerExtrasTotal = 0;
    pickerTypes.forEach(function(t) {
      pickerExtrasTotal += computeExtrasTotalForType(t);
    });

    var addonsTotal = 0;
    state.addons.forEach(function(qty, addon) {
      if (addonPrices[addon]) addonsTotal += addonPrices[addon] * qty;
    });

    var grandTotal = baseTotal + pickerExtrasTotal + addonsTotal;

    var totalEl = document.querySelector('[data-config-total]');
    if (totalEl) totalEl.textContent = formatPrice(grandTotal);

    var perheadEl = document.querySelector('[data-config-perhead]');
    if (perheadEl) {
      var text = formatPrice(perHead) + '/head';
      var combinedExtras = pickerExtrasTotal + addonsTotal;
      if (combinedExtras > 0) text += ' + ' + formatPrice(combinedExtras) + ' extras';
      perheadEl.textContent = text;
    }

    updateFreeShipTracker(grandTotal);
  }

  // ══════════════════════════════════════════
  // ── SIZE-SCALED EXTRAS ──
  // ══════════════════════════════════════════
  //
  // Most extras are a single thing at a single price. The Hosting Pack is not:
  // it is priced per head, so a table of 2 pays £6/head and a table of 8 pays
  // £4/head. That is modelled as one product with a variant per group size,
  // and this re-points the addon at the right variant whenever the size
  // changes — updating the price shown on the card and the price used in the
  // running total and at cart-add.
  //
  // A variant matches when its title contains the group size as a whole number
  // ("For 4", "4 people", "4"). If nothing matches we leave the addon on its
  // first variant rather than guessing, so a mis-titled variant shows a
  // possibly-stale price instead of silently adding the wrong thing to a cart.

  function findVariantForSize(variants, size) {
    if (!variants || !variants.length) return null;
    var re = new RegExp('(^|[^0-9])' + size + '([^0-9]|$)');
    for (var i = 0; i < variants.length; i++) {
      if (re.test(String(variants[i].title || ''))) return variants[i];
    }
    return null;
  }

  function resolveSizeScaledAddon(addonKey) {
    var rec = productData.addons[addonKey];
    if (!rec || !rec.variants || rec.variants.length < 2) return;
    var card = document.querySelector('[data-addon="' + addonKey + '"]');
    // The Hosting Pack is always per size, whatever its block's "Price scales with
    // group size" box says (the templates save it off, which charged For-2 prices).
    var scaled = (card && card.dataset.sizeScaled === 'true') || rec.handle === 'wf-hosting-pack';
    if (!scaled) return;

    var match = findVariantForSize(rec.variants, state.groupSize);
    if (!match) {
      console.warn('[Configurator] Size-scaled extra "' + addonKey + '" has no variant for ' + state.groupSize + ' people — leaving it on ' + rec.variants[0].title);
      return;
    }
    rec.variantId = match.id;
    rec.price = match.price;
    rec.available = rec.productAvailable !== false && match.available !== false;
    addonPrices[addonKey] = match.price;

    // Reflect the new price on the card, which renders "· £X" after the copy.
    var meta = card && card.querySelector('.addon-meta');
    if (meta && meta.dataset.baseText === undefined) meta.dataset.baseText = meta.textContent.replace(/\s*·\s*£[\d.,]+\s*$/, '');
    if (meta) meta.textContent = meta.dataset.baseText + ' · ' + formatPrice(match.price);
  }

  function resolveAllSizeScaledAddons() {
    Object.keys(productData.addons).forEach(resolveSizeScaledAddon);
  }

  // Free-shipping upsell prompt — lives in the sticky footer so it's visible
  // while the customer is still picking mains / sides / addons (not just after
  // they hit the cart drawer, by which point adding more is too much friction).
  // Shows a progress bar + "Spend £X more for free delivery" message; flips to
  // "✓ Free delivery on this order" once the order qualifies.
  function updateFreeShipTracker(grandTotal) {
    var wrap = document.querySelector('[data-freeship]');
    if (!wrap) return;
    var threshold = pricing.freeShippingThreshold;
    if (!threshold) { wrap.hidden = true; return; }
    wrap.hidden = false;
    var pct = Math.min(100, Math.max(0, Math.round((grandTotal / threshold) * 100)));
    var fill = wrap.querySelector('[data-freeship-fill]');
    var text = wrap.querySelector('[data-freeship-text]');
    if (fill) fill.style.width = pct + '%';
    if (satDateSelected()) {
      wrap.classList.remove('is-unlocked');
      if (text) text.textContent = SAT_LINE;
    } else if (grandTotal >= threshold) {
      wrap.classList.add('is-unlocked');
      if (text) text.textContent = 'Free delivery on this order';
    } else {
      wrap.classList.remove('is-unlocked');
      var remaining = threshold - grandTotal;
      if (text) text.textContent = 'Spend ' + formatPrice(remaining) + ' more for free delivery';
    }
  }

  // ══════════════════════════════════════════
  // ── ADD-ON QUANTITY SELECTORS ──
  // ══════════════════════════════════════════
  // Each addon card carries its own − N + selector (matches the main-dishes
  // step). Replaces the older +Add / ✓Added toggle so customers can buy
  // multiples of each extra — the whole point of the upsell step.
  // No upper cap by design, mirroring the dish picker (sold-out is the
  // only hard limit — caught on cart-add).

  function setAddonQty(card, qty) {
    var addon = card.dataset.addon;
    var num = card.querySelector('[data-addon-qty-value]');
    if (qty <= 0) {
      state.addons.delete(addon);
      card.classList.remove('is-added');
      if (num) num.textContent = '0';
    } else {
      state.addons.set(addon, qty);
      card.classList.add('is-added');
      if (num) num.textContent = String(qty);
    }
    updateRunningTotal();
  }

  document.querySelectorAll('[data-addon-qty-plus]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var card = this.closest('.cuisine-addon-card');
      if (!card || card.classList.contains('is-sold-out') || card.dataset.soldOut === 'true') return;
      var addon = card.dataset.addon;
      var current = state.addons.get(addon) || 0;
      var addonStock = stockOf(card);
      if (addonStock !== null && variantDemand(cardVariantNum(card)) >= addonStock) {
        var addonLim = card.querySelector('[data-stock-limit]');
        if (addonLim && addonStock > 0) addonLim.hidden = false;
        return;
      }
      setAddonQty(card, current + 1);
    });
  });

  document.querySelectorAll('[data-addon-qty-minus]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var card = this.closest('.cuisine-addon-card');
      if (!card || card.classList.contains('is-sold-out') || card.dataset.soldOut === 'true') return;
      var addon = card.dataset.addon;
      var current = state.addons.get(addon) || 0;
      if (current <= 0) return;
      setAddonQty(card, current - 1);
    });
  });

  // ══════════════════════════════════════════
  // ── DATE PICKER ──
  // Selectable days, cutoff, blackouts, and extra dates are owner-editable
  // in the theme editor: Online Store → Themes → Customize → Theme
  // settings → "Delivery dates". Liquid emits the values as
  // window.WF_DELIVERY_CONFIG in layout/theme.liquid.
  // ══════════════════════════════════════════

  var deliveryConfig = window.WF_DELIVERY_CONFIG || {};
  var cutoffDayOfWeek = parseInt(deliveryConfig.cutoffDayOfWeek, 10);
  if (isNaN(cutoffDayOfWeek) || cutoffDayOfWeek < 0 || cutoffDayOfWeek > 6) cutoffDayOfWeek = 3;
  var cutoffTimeParts = (deliveryConfig.cutoffTime || '12:00').split(':');
  var cutoffHour = parseInt(cutoffTimeParts[0], 10);
  var cutoffMinute = parseInt(cutoffTimeParts[1], 10);
  if (isNaN(cutoffHour) || cutoffHour < 0 || cutoffHour > 23) cutoffHour = 12;
  if (isNaN(cutoffMinute) || cutoffMinute < 0 || cutoffMinute > 59) cutoffMinute = 0;

  function parseDateList(raw) {
    if (!raw) return {};
    var out = {};
    raw.split(/[\s,]+/).forEach(function(s) {
      var m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) out[m[0]] = true;
    });
    return out;
  }
  var blackoutDates = parseDateList(deliveryConfig.blackoutDates);
  var extraDates = parseDateList(deliveryConfig.extraDates);

  // ── Saturday delivery (Q2). Switched by the theme setting (on from 16 Oct 2026).
  // ?wf_sat_preview=1 forces it on for THIS page view only, so Ben and we
  // can preview the Saturday cards before enabling for customers.
  var saturdayEnabled = deliveryConfig.saturdayEnabled === true
    || /[?&]wf_sat_preview=1/.test(location.search || '');
  var satCutoffDay = parseInt(deliveryConfig.saturdayCutoffDay, 10);
  if (isNaN(satCutoffDay) || satCutoffDay < 0 || satCutoffDay > 6) satCutoffDay = 4;
  var satCutoffParts = (deliveryConfig.saturdayCutoffTime || '12:00').split(':');
  var satCutoffHour = parseInt(satCutoffParts[0], 10);
  var satCutoffMinute = parseInt(satCutoffParts[1], 10);
  if (isNaN(satCutoffHour) || satCutoffHour < 0 || satCutoffHour > 23) satCutoffHour = 12;
  if (isNaN(satCutoffMinute) || satCutoffMinute < 0 || satCutoffMinute > 59) satCutoffMinute = 0;

  function isDeliveryWeekday(date) {
    var d = date.getDay();
    return d === 5 || (saturdayEnabled && d === 6);
  }

  // For a candidate delivery date D, the order window closes at the most
  // recent {cutoffDayOfWeek} strictly before D, at {cutoffHour}:{cutoffMinute}.
  // e.g. cutoffDay=Wed, cutoffTime=12:00, D=Fri → cutoff = Wed 12:00 of the
  // same week (2 days before D).
  function getCutoffForDelivery(deliveryDate) {
    // Saturdays get their own cutoff rule (Thu 12:00); everything
    // else (Fridays + owner extra dates) keeps the main rule (Wed 12:00).
    var cDay = cutoffDayOfWeek, cHour = cutoffHour, cMin = cutoffMinute;
    if (saturdayEnabled && deliveryDate.getDay() === 6) {
      cDay = satCutoffDay; cHour = satCutoffHour; cMin = satCutoffMinute;
    }
    var daysBack = ((deliveryDate.getDay() - cDay) + 7) % 7;
    if (daysBack === 0) daysBack = 7;
    var cutoff = new Date(deliveryDate);
    cutoff.setDate(cutoff.getDate() - daysBack);
    cutoff.setHours(cHour, cMin, 0, 0);
    return cutoff;
  }

  function isoString(date) {
    var mm = String(date.getMonth() + 1).padStart(2, '0');
    var dd = String(date.getDate()).padStart(2, '0');
    return date.getFullYear() + '-' + mm + '-' + dd;
  }

  // Cut-offs are UK times: read "now" as the Europe/London wall clock (see
  // WF_LONDON_NOW in layout/theme.liquid) so a visitor in another timezone
  // sees the same open/closed dates as someone in the UK. Falls back to the
  // device clock if the helper is missing.
  function londonNow() { return (window.WF_LONDON_NOW ? window.WF_LONDON_NOW() : new Date()); }
  var now = londonNow();
  var calYear = now.getFullYear(), calMonth = now.getMonth();
  // Open the calendar on the month that actually contains the soonest
  // pickable delivery date, instead of the old `now.getDate() > 15` heuristic
  // that blindly jumped to next month after the 15th. That heuristic routinely
  // opened the calendar a full month PAST the earliest bookable Friday, so a
  // customer browsing late in the month landed on a calendar whose soonest
  // Friday was weeks out and had to page BACKWARD to find the real one — the
  // single biggest drop-off in the funnel (dish_picked -> date_selected).
  // Scan forward day-by-day (bounded) using the exact same availability rules
  // renderCalendar() applies, and land on the first genuinely pickable day.
  (function openOnEarliestPickable() {
    var probe = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (var i = 0; i < 120; i++) {
      var iso = isoString(probe);
      var isDel = isDeliveryWeekday(probe);
      var isExtra = !!extraDates[iso];
      var isBlackout = !!blackoutDates[iso];
      if ((isDel || isExtra) && !isBlackout && now < getCutoffForDelivery(probe)) {
        calYear = probe.getFullYear();
        calMonth = probe.getMonth();
        return;
      }
      probe.setDate(probe.getDate() + 1);
    }
    // No pickable date within 120 days (shouldn't happen given weekly Fridays)
    // — leave calYear/calMonth on the current real month as a safe fallback.
  })();

  // How many delivery-date cards to show. Grows in steps via "See more".
  var datesShown = 4;
  var DATES_STEP = 4;

  // ══════════════════════════════════════════
  // ── DATE-STEP INSTRUMENTATION ──
  // ══════════════════════════════════════════
  //
  // The delivery-date step is where the funnel dies: across 17 days, 85
  // sessions reached it and 21 picked a date. Rage-click data says it isn't
  // broken (1 rage session here vs 15-16 on the size and tier steps), so people
  // are looking at it and deciding not to continue. There are three candidate
  // reasons and the events below are designed to tell them apart:
  //
  //   "too far away"  — the soonest Friday is past the weekend they had in mind.
  //                     Look for a high days_to_soonest on abandoned sessions,
  //                     and for pickers clustering on the first offered date.
  //   "Friday means Friday" — they think the food is only good on delivery day,
  //                     so a Saturday host rules us out. Look for short dwell
  //                     and no interaction at all before leaving.
  //   "not ready to commit" — they need to check who's coming. Look for long
  //                     dwell, and for see-more clicks, before leaving.
  //
  // Deliberately no A/B test: ~5 sessions a day reach this step, so a split
  // test could not reach significance in any useful timeframe. These events
  // exist to be read alongside session recordings, not to power an experiment.

  var dateStepEnteredAt = 0;      // ms timestamp of arrival, 0 = not on the step
  var dateStepSawMore = false;    // did they ask for more Fridays this visit?
  var dateStepReported = false;   // guard so abandonment is only ever sent once

  // Whole days from today to a delivery date. 0 would mean "today", which the
  // cutoff rules make impossible, so the smallest real value is 1.
  function daysUntil(date) {
    var today = londonNow();
    today = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((date - today) / 86400000);
  }

  // Shared shape for every date-step event, so the rollup can group them
  // without re-deriving context per event type.
  function dateStepContext() {
    var offered = collectDeliveryDates(datesShown);
    return {
      days_to_soonest: offered.length ? daysUntil(offered[0]) : null,
      soonest: offered.length ? isoString(offered[0]) : null,
      offered: offered.length,
      saw_more: dateStepSawMore,
      path: state.tier || null,
      size: state.groupSize,
      cuisine: state.cuisine || null
    };
  }

  // Fired when someone leaves the date step without choosing — the population
  // we actually need to understand. sendBeacon (see window.WF.event) is what
  // makes this survive the unload.
  function reportDateStepAbandon(reason) {
    if (!dateStepEnteredAt || dateStepReported || state.selectedDate) return;
    dateStepReported = true;
    if (!(window.WF && window.WF.event)) return;
    var ctx = dateStepContext();
    ctx.reason = reason;
    ctx.dwell_ms = Date.now() - dateStepEnteredAt;
    window.WF.event('date_step_abandoned', ctx);
  }

  // pagehide rather than unload/beforeunload: it is the only one of the three
  // that fires reliably on mobile Safari, which is most of our traffic.
  //
  // Deliberately NOT visibilitychange. Switching away from this step is a
  // normal thing to do here — checking a calendar, asking whoever's coming —
  // and counting that as abandonment would poison the exact number we're
  // adding these events to measure.
  window.addEventListener('pagehide', function() { reportDateStepAbandon('left_page'); });

  // Cut-off time label for a given delivery date (Saturdays have their own).
  function fmtCutoffTime(deliveryDate, hOverride, mOverride) {
    var h = cutoffHour, m = cutoffMinute;
    if (hOverride != null) { h = hOverride; m = mOverride || 0; }
    else if (deliveryDate && saturdayEnabled && deliveryDate.getDay() === 6) { h = satCutoffHour; m = satCutoffMinute; }
    var ampm = h >= 12 ? 'pm' : 'am';
    var hh = h % 12; if (hh === 0) hh = 12;
    return hh + (m ? ':' + String(m).padStart(2, '0') : '') + ampm;
  }

  // One canonical delivery sentence for the date step, built from the same
  // settings that drive the cards (so it can never disagree with them).
  window.WF_DELIVERY_HELP_HTML = function() {
    var dn = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    function when(d, h, m) {
      var t = (h === 12 && !m) ? 'midday' : fmtCutoffTime({ getDay: function() { return -1; } }, h, m);
      return dn[d] + ' ' + t;
    }
    var out = 'All food is prepared to order. ';
    if (saturdayEnabled) {
      out += 'Order by ' + when(cutoffDayOfWeek, cutoffHour, cutoffMinute) + ' for Friday delivery, or ' +
        when(satCutoffDay, satCutoffHour, satCutoffMinute) + ' for Saturday (&pound;10, or &pound;20 for before 12:00; &pound;5.99 less on orders over &pound;65). ';
    } else {
      out += 'Order by ' + when(cutoffDayOfWeek, cutoffHour, cutoffMinute) + ' for Friday delivery. ';
    }
    out += '<strong>Your Feast keeps in the fridge until Sunday.</strong> <span class="cuisine-date-help__dpd">DPD give you a one-hour slot on the day.</span>';
    return out;
  };

  // Collect the next `count` genuinely-pickable delivery dates — Fridays (or
  // owner-added extra dates), minus blackouts, with the order window still open.
  // Same rules the old month calendar used, just scanned forward into a list.
  function collectDeliveryDates(count) {
    var out = [];
    var probe = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var nowMoment = londonNow();
    probe = new Date(nowMoment.getFullYear(), nowMoment.getMonth(), nowMoment.getDate());
    for (var i = 0; i < 400 && out.length < count; i++) {
      var iso = isoString(probe);
      var isDel = isDeliveryWeekday(probe);
      var isExtra = !!extraDates[iso];
      var isBlackout = !!blackoutDates[iso];
      if ((isDel || isExtra) && !isBlackout && nowMoment < getCutoffForDelivery(probe)) {
        out.push(new Date(probe.getFullYear(), probe.getMonth(), probe.getDate()));
      }
      probe.setDate(probe.getDate() + 1);
    }
    return out;
  }

  // Render the "next Fridays" cards (replaces the month calendar). Keeps the
  // same data-date + selectDate wiring, so selection, the summary and restore
  // all work through the existing code. Named renderCalendar so all callers
  // (init, restore, and the now-dormant prev/next handlers) resolve unchanged.
  function renderCalendar() {
    var list = document.querySelector('[data-fri-list]');
    if (!list) return;
    var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    var shortMonths = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    var days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    var shortDays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    var todayIso = isoString(londonNow());
    var dates = collectDeliveryDates(datesShown);

    // "Soonest" badge goes on the card autoPreselectDate() will arm (the first
    // non-Saturday), so a Saturday that opens first never looks chosen too.
    var badgeIdx = 0;
    for (var bi = 0; bi < dates.length; bi++) { if (dates[bi].getDay() !== 6) { badgeIdx = bi; break; } }

    list.innerHTML = '';
    dates.forEach(function(date, idx) {
      var iso = isoString(date);
      var cutoff = getCutoffForDelivery(date);
      var cutoffIsToday = isoString(cutoff) === todayIso;
      var dayName = days[date.getDay()];
      var selected = state.selectedDate === iso;

      var card = document.createElement('button');
      card.type = 'button';
      card.className = 'cuisine-fri-card' + (selected ? ' is-selected' : '');
      card.dataset.date = iso;
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');
      card.setAttribute('aria-label', dayName + ' ' + date.getDate() + ' ' + months[date.getMonth()] + ' ' + date.getFullYear());

      var cutLabel = cutoffIsToday
        ? 'Order by today, ' + fmtCutoffTime(date)
        : 'Order by ' + shortDays[cutoff.getDay()] + ' ' + cutoff.getDate() + ' ' + shortMonths[cutoff.getMonth()];

      var satHint = (date.getDay() === 6 && saturdayEnabled && deliveryConfig.saturdayHint)
        ? '<span class="cuisine-fri-card__hint">' + String(deliveryConfig.saturdayHint).replace(/[<>&"]/g, '') + '</span>' : '';

      card.innerHTML =
        (idx === badgeIdx ? '<span class="cuisine-fri-card__badge">' + (cutoffIsToday ? 'This week' : 'Soonest') + '</span>' : '') +
        '<span class="cuisine-fri-card__check" aria-hidden="true">✓</span>' +
        '<span class="cuisine-fri-card__day">' + dayName + '</span>' +
        '<span class="cuisine-fri-card__date">' + date.getDate() + ' ' + shortMonths[date.getMonth()] + '</span>' +
        '<span class="cuisine-fri-card__cut">' + cutLabel + '</span>' + satHint;

      card.addEventListener('click', selectDate);
      list.appendChild(card);
    });

    // Hide "See more" once we've run out of upcoming dates to show.
    var moreBtn = document.querySelector('[data-fri-more]');
    if (moreBtn) moreBtn.style.display = (dates.length < datesShown) ? 'none' : '';
  }

  // True only while autoPreselectDate() is arming the soonest Friday on the
  // customer's behalf. Guards the telemetry: an automatic selection must never
  // count as a deliberate date_selected, or the date-step leak metric — the
  // number this whole redesign is being judged by — becomes unmeasurable.
  var isAutoPreselect = false;

  // Ben (2026-09-01): the "Soonest" card carried a badge and a check glyph so
  // it LOOKED chosen, but nothing was selected and Add to Cart stayed dark
  // until a redundant click. Arm the step for real on arrival instead.
  function autoPreselectDate() {
    if (state.selectedDate) return;
    // Arm the soonest FRIDAY, not a Saturday: Saturday carries a delivery
    // charge, so it must be a deliberate pick. Falls back to the first card.
    var cards = document.querySelectorAll('[data-fri-list] .cuisine-fri-card');
    var first = null;
    for (var ci = 0; ci < cards.length && !first; ci++) {
      var ds = (cards[ci].dataset.date || '').split('-');
      var cd = new Date(parseInt(ds[0], 10), parseInt(ds[1], 10) - 1, parseInt(ds[2], 10));
      if (cd.getDay() !== 6) first = cards[ci];
    }
    if (!first) first = cards[0];
    if (!first) return;
    isAutoPreselect = true;
    try { first.click(); } finally { isAutoPreselect = false; }
  }

  function selectDate(e) {
    var target = e.currentTarget || e.target;
    state.selectedDate = target.dataset.date;
    // Restore-replay guard: restorePersistedState() calls dayCell.click() to
    // replay the saved selection — without this it would emit a duplicate
    // date_selected on every page load for returning visitors.
    if (!isRestoringState && !isAutoPreselect && window.WF && window.WF.event) {
      // Which of the offered Fridays they took, and how far out it is. If
      // almost everyone takes the first one, lead time is our binding
      // constraint; if they routinely reach for the third or fourth, they are
      // planning ahead and lead time is not what is costing us the other 75%.
      var offered = collectDeliveryDates(datesShown);
      var pickedIndex = offered.map(isoString).indexOf(state.selectedDate);
      window.WF.event('date_selected', {
        date: state.selectedDate,
        tier: state.tier,
        size: state.groupSize,
        cuisine: state.cuisine || null,
        picked_index: pickedIndex >= 0 ? pickedIndex + 1 : null,
        days_out: pickedIndex >= 0 ? daysUntil(offered[pickedIndex]) : null,
        saw_more: dateStepSawMore,
        dwell_ms: dateStepEnteredAt ? Date.now() - dateStepEnteredAt : null
      });
    }
    if (isAutoPreselect) {
      // Distinct event so the rollup can tell "accepted the preselect" from
      // "deliberately chose". Abandon tracking stays live — an auto-armed
      // customer who leaves is still an abandonment worth counting.
      if (!isRestoringState && window.WF && window.WF.event) {
        window.WF.event('date_preselected', {
          date: state.selectedDate,
          tier: state.tier,
          size: state.groupSize,
          cuisine: state.cuisine || null
        });
      }
    } else {
      dateStepReported = true; // chose a date — never report this visit as abandoned
    }

    document.querySelectorAll('.cuisine-fri-card.is-selected').forEach(function(el) {
      el.classList.remove('is-selected');
      el.setAttribute('aria-pressed', 'false');
    });
    target.classList.add('is-selected');
    target.setAttribute('aria-pressed', 'true');
    syncFooterDate();
    updatePricing(); // delivery lines depend on Friday vs Saturday

    var parts = state.selectedDate.split('-');
    var date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    var months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    var days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

    var selectedEl = document.querySelector('[data-date-selected]');
    if (selectedEl) selectedEl.textContent = days[date.getDay()] + ' ' + date.getDate() + ' ' + months[date.getMonth()];

    var shortDays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    var dispatch = new Date(date);
    dispatch.setDate(dispatch.getDate() - 1);
    var dispatchEl = document.querySelector('[data-date-dispatch]');
    if (dispatchEl) dispatchEl.textContent = shortDays[dispatch.getDay()] + ' ' + dispatch.getDate() + ' ' + months[dispatch.getMonth()];

    var cutoff = getCutoffForDelivery(date);
    var cutoffEl = document.querySelector('[data-date-cutoff]');
    if (cutoffEl) cutoffEl.textContent = shortDays[cutoff.getDay()] + ' ' + cutoff.getDate() + ' ' + months[cutoff.getMonth()];

    console.log('[Configurator] Delivery date selected:', state.selectedDate);
    updateFooterButton();
    updateProgressBar(); // picking a date completes the final step → fill to 100%
    persistState();
  }

  // "See more Fridays" — reveal the next batch of upcoming delivery dates.
  var friMore = document.querySelector('[data-fri-more]');
  if (friMore) friMore.addEventListener('click', function() {
    datesShown += DATES_STEP;
    dateStepSawMore = true;
    renderCalendar();
    if (window.WF && window.WF.event) {
      var moreCtx = dateStepContext();
      moreCtx.shown = datesShown;
      moreCtx.dwell_ms = dateStepEnteredAt ? Date.now() - dateStepEnteredAt : null;
      window.WF.event('date_see_more', moreCtx);
    }
  });

  // ══════════════════════════════════════════
  // ── SHOPIFY CART INTEGRATION ──
  // ══════════════════════════════════════════

  // Map picker type to a human-readable property label for cart line items
  var typePropLabels = {
    'main': 'Mains',
    'side': 'Sides',
    'sauce': 'Sauces',
    'bread': 'Breads'
  };

  // Build the kitchen-facing summary string for a picker category.
  // Only "included" picks — items beyond the minimum ship as separate cart
  // lines and the kitchen sees them there, so we don't duplicate them here.
  function buildSelectionSummary(type) {
    var included = getIncludedAndExtras(type).included;
    var summary = [];
    Object.keys(included).forEach(function(dish) {
      var qty = included[dish];
      if (qty > 0) {
        summary.push(getDishLabel(dish) + (qty > 1 ? ' x' + qty : ''));
      }
    });
    return summary.join(', ');
  }

  function buildLineItemProperties() {
    // No underscore prefix — these need to be visible in confirmation emails
    // and on the order status page for both customers and the kitchen
    var props = {
      'Tier': tierLabel(),
      'Group Size': state.groupSize + ' people'
    };

    // The box has no picker selections to summarise, so describe its composed
    // contents instead — the kitchen and the confirmation email should read the
    // same whichever path the customer took.
    if (state.tier === 'box') {
      var boxSummary = {};
      var boxOrder = [];
      document.querySelectorAll('[data-tier="box"] [data-box-dish]').forEach(function(row) {
        if (row.hidden) return;
        var nameEl = row.querySelector('.cuisine-box-dish__name span');
        if (!nameEl) return;
        var name = nameEl.childNodes[0] ? String(nameEl.childNodes[0].textContent).trim() : nameEl.textContent.trim();
        var qtyEl = row.querySelector('[data-box-dish-qty]');
        var qty = 1;
        if (qtyEl && !qtyEl.hidden) {
          var parsed = parseInt(String(qtyEl.textContent).replace(/[^0-9]/g, ''), 10);
          if (parsed > 0) qty = parsed;
        }
        var cat = row.getAttribute('data-box-cat') || 'other';
        if (!boxSummary[cat]) { boxSummary[cat] = []; boxOrder.push(cat); }
        boxSummary[cat].push(qty > 1 ? name + ' x' + qty : name);
      });
      boxOrder.forEach(function(cat) {
        var label = typePropLabels[cat] || (cat.charAt(0).toUpperCase() + cat.slice(1) + 's');
        props[label] = boxSummary[cat].join(', ');
      });
    }

    // Dynamically add properties for each picker type that exists
    pickerTypes.forEach(function(type) {
      var label = typePropLabels[type] || (type.charAt(0).toUpperCase() + type.slice(1) + 's');
      var summary = buildSelectionSummary(type);
      if (summary) {
        props[label] = summary;
      }
    });

    props['Delivery Date'] = state.selectedDate || 'Not selected';
    // Delivery window (Standard 8am–6pm vs Express AM before 12pm) is a Shopify
    // shipping rate chosen at checkout, not a configurator state — so it's not
    // a line-item property. The customer's checkout-selected rate is the
    // source of truth and appears on the order naturally.

    var addonNames = [];
    state.addons.forEach(function(qty, addon) {
      var card = document.querySelector('[data-addon="' + addon + '"]');
      if (card) {
        var nameEl = card.querySelector('h4');
        if (nameEl) {
          var name = nameEl.textContent.trim();
          // Append × N when qty > 1 so the order confirmation shows the
          // count for each extra at a glance.
          addonNames.push(qty > 1 ? name + ' × ' + qty : name);
        }
      }
    });
    if (addonNames.length > 0) {
      props['Add-ons'] = addonNames.join(', ');
    }

    // ── Cart Transform Function picks ──
    // The Cart Transform Function (worldfeast-bundle-function repo) reads a
    // single line property `_wf_picks` containing a JSON-encoded array of the
    // customer's chosen variant GIDs and expands the bundle line into one
    // component line per pick. We send only the "included" picks (first N
    // per category); à la carte extras are added as separate cart lines
    // priced at retail, so the bundle composition stays at exactly N.
    // Underscore prefix hides the property from the customer-facing cart UI;
    // safe no-op while the function isn't deployed.
    var picks = [];
    if (state.tier === 'box') {
      // The box is composed for us, not picked by the customer, but the Cart
      // Transform Function still needs the component variants so the kitchen
      // gets one line per dish exactly as it would for a hand-built feast.
      picks = boxPicks.slice();
    }
    pickerTypes.forEach(function(type) {
      var included = getIncludedAndExtras(type).included;
      var slotIndex = 1;
      Object.keys(included).forEach(function(dishId) {
        var count = included[dishId] || 0;
        if (count <= 0) return;
        var card = document.querySelector('[data-picker="' + type + '"][data-dish="' + dishId + '"]');
        var variantGid = card && card.getAttribute('data-variant-id');
        if (!variantGid) return;
        for (var n = 0; n < count; n++) {
          picks.push({ slot: type + '_' + slotIndex, variantId: variantGid });
          slotIndex++;
        }
      });
    });
    if (picks.length > 0) {
      props['_wf_picks'] = JSON.stringify(picks);
    }
    // Durable analytics attribution for the parent bundle line. Keep this
    // outside the picks guard: even a malformed or temporarily empty picks
    // payload still came from the configurator. Extras and add-ons build
    // separate property objects in buildCartItems and intentionally omit it.
    props['_wf_source'] = 'configurator';

    return props;
  }

  function buildCartItems() {
    var items = [];
    // Pick the bundle by tier × size when tier-keyed (Cart Transform path).
    // Falls back to size-only lookup for the legacy structure.
    var bundleInfo;
    if (isTierKeyedBundles) {
      var tier = state.tier === 'box' ? 'box' : 'feast';
      bundleInfo = productData.bundles[tier] && productData.bundles[tier][String(state.groupSize)];
    } else {
      bundleInfo = productData.bundles[String(state.groupSize)];
    }

    if (!bundleInfo || !bundleInfo.variantId || bundleInfo.variantId === 0) {
      console.error('[Configurator] No valid bundle for tier/size', state.tier, state.groupSize, bundleInfo);
      return { error: 'Sorry, we couldn\'t find the right product for ' + state.groupSize + ' people. Please contact us.' };
    }

    if (bundleInfo.available === false) {
      console.warn('[Configurator] Bundle is sold out:', bundleInfo.handle || bundleInfo.title);
      var soldOutLabel = tierLabel();
      return { error: 'The ' + soldOutLabel + ' for ' + state.groupSize + ' is temporarily sold out. Please try a different group size or tier — we can also help you directly, just hit "Speak To Us".' };
    }

    var properties = buildLineItemProperties();
    var bundleQty = bundleInfo.quantity || 1;

    items.push({
      id: bundleInfo.variantId,
      quantity: bundleQty,
      properties: properties
    });

    console.log('[Configurator] Bundle item:', bundleInfo.title, '× ' + bundleQty, 'variant:', bundleInfo.variantId);

    // À la carte extras: any picker selections beyond the included minimum
    // ship as standalone cart lines at the dish's retail price. The Cart
    // Transform Function only operates on the bundle line, so extras pass
    // through to checkout untouched. Tagged with "Part of" so the slide cart
    // shows them visually associated with the parent feast.
    pickerTypes.forEach(function(type) {
      var extras = getIncludedAndExtras(type).extras;
      Object.keys(extras).forEach(function(dishId) {
        var count = extras[dishId];
        if (!count || count <= 0) return;
        var card = document.querySelector('[data-picker="' + type + '"][data-dish="' + dishId + '"]');
        if (!card) return;
        var variantNum = parseInt(card.dataset.variantIdNumeric, 10);
        if (!variantNum) {
          var gid = card.getAttribute('data-variant-id');
          if (gid) variantNum = parseInt(gid.split('/').pop(), 10);
        }
        if (!variantNum) {
          console.warn('[Configurator] Extra "' + type + '/' + dishId + '" has no variant id, skipping');
          return;
        }
        var typeLabel = typePropLabels[type] || (type.charAt(0).toUpperCase() + type.slice(1));
        items.push({
          id: variantNum,
          quantity: count,
          properties: {
            'Part of': properties['Tier'] + ' for ' + state.groupSize + ' people',
            'Added as extra': typeLabel.replace(/s$/, '')
          }
        });
        console.log('[Configurator] Extra item:', type, dishId, '× ' + count, 'variant:', variantNum);
      });
    });

    // Add-on products as separate line items, one Shopify line each carrying
    // the customer's chosen quantity. Silently skip any that have flipped
    // to sold-out since the customer added them — better to add what we
    // can than fail the whole cart-add.
    var immersiveOrder = isImmersiveOrder();
    state.addons.forEach(function(qty, addon) {
      var addonInfo = productData.addons[addon];
      if (addonInfo && addonInfo.variantId && addonInfo.variantId !== 0 && addonInfo.available !== false) {
        var addonProps = {
          'Part of': properties['Tier'] + ' for ' + state.groupSize + ' people'
        };
        // Hidden marker so Immersive Feast orders can be counted (the cart
        // attribute _wf_tier is written with _wf_journey in layout/theme.liquid).
        if (immersiveOrder && addon === immPackKey) addonProps['_wf_tier'] = 'immersive';
        items.push({
          id: addonInfo.variantId,
          quantity: qty,
          properties: addonProps
        });
        console.log('[Configurator] Add-on item:', addon, '× ' + qty, 'variant:', addonInfo.variantId);
      } else if (addonInfo && addonInfo.available === false) {
        console.warn('[Configurator] Skipping sold-out addon:', addon);
        state.addons.delete(addon);
      } else {
        console.warn('[Configurator] Add-on "' + addon + '" has no valid variant ID, skipping');
      }
    });

    return { items: items };
  }

  function addToCart() {
    if (state.isAddingToCart) return;

    // ── Pre-flight validation ──
    // Each early-return below emits an addtocart_blocked telemetry event with
    // a reason. The footer button SHOULD be disabled in these states (the UI
    // gates each path), so any hits here represent either a UI/state desync
    // or a customer who clicked through a transient enabled state — worth
    // tracking so we can spot regressions.
    function emitBlocked(reason) {
      if (window.WF && window.WF.event) {
        window.WF.event('addtocart_blocked', {
          reason: reason,
          tier: state.tier || null,
          size: state.groupSize,
          cuisine: state.cuisine || null
        });
      }
    }

    if (!dataReady) {
      console.error('[Configurator] Product data not loaded yet');
      emitBlocked('data_not_ready');
      return;
    }

    if (!state.tier) {
      console.error('[Configurator] No tier selected');
      emitBlocked('no_tier');
      return;
    }

    // Validate each picker step — navigate to first incomplete step instead of
    // alerting. Build-Your-Own only: the box has no picker steps to satisfy,
    // and running this against it bounced the customer back to step 1.
    for (var i = 0; state.tier !== 'box' && i < pickerTypes.length; i++) {
      var type = pickerTypes[i];
      var count = getTotalForType(type);
      var req = getRequiredCount(type);
      if (count < req) {
        emitBlocked('picker_incomplete:' + type);
        showStep(typeStepMap[type]);
        if (configuratorEl) scrollToBuilder();
        return;
      }
    }

    if (!state.selectedDate) {
      emitBlocked('no_date');
      showStep(typeStepMap['date'] || state.totalSteps);
      if (configuratorEl) scrollToBuilder();
      return;
    }

    // Look up the bundle handle BEFORE building cart items. We need this for
    // the preflight refresh, which has two jobs:
    //   1. Catch race where the bundle sold out between page load and ATC.
    //   2. Recover any init-fetch stub (variantId=0) by populating it with
    //      a real variant id from the refetched product.
    //
    // buildCartItems must run AFTER preflight, because its variantId guard
    // would reject a stub entry with "couldn't find the right product" even
    // if preflight would have recovered it.
    var bundleHandleForRefresh = null;
    if (isTierKeyedBundles) {
      var t = state.tier === 'box' ? 'box' : 'feast';
      var info = productData.bundles[t] && productData.bundles[t][String(state.groupSize)];
      bundleHandleForRefresh = info && info.handle;
    } else {
      var legacyInfo = productData.bundles[String(state.groupSize)];
      bundleHandleForRefresh = legacyInfo && legacyInfo.handle;
    }

    state.isAddingToCart = true;
    if (window.WF && window.WF.event) window.WF.event('addtocart_attempt', { tier: state.tier, size: state.groupSize, cuisine: state.cuisine || null, bundleHandle: bundleHandleForRefresh || null, date: state.selectedDate || null });
    var btn = document.querySelector('[data-config-cart]');
    var originalText = btn ? btn.textContent : '';
    if (btn) {
      btn.textContent = 'Checking availability...';
      btn.disabled = true;
    }

    var preflight = bundleHandleForRefresh
      ? fetchProduct(bundleHandleForRefresh).then(function(p) {
          if (p && p.available === false) {
            // Mutate local state so the UI reflects the new sold-out state on retry
            var entry = getBundleInfo(state.tier, state.groupSize);
            if (entry) entry.available = false;
            refreshAvailabilityUI();
            throw new Error('SOLD_OUT_PREFLIGHT');
          }
          // If the init-time fetch failed and the entry is a stub, populate
          // it now from this successful refetch so submitCartItems sees a
          // real variantId. Without this, buildCartItems would reject with
          // "couldn't find the right product" even though the bundle is fine.
          var entry = getBundleInfo(state.tier, state.groupSize);
          if (entry && entry._fetchFailed && p && p.variants && p.variants.length > 0) {
            var variant = p.variants[0];
            entry.variantId = variant.id;
            entry.productId = p.id;
            entry.price = Math.round(parseFloat(variant.price) * 100);
            entry.title = p.title;
            entry.available = p.available !== false && variant.available !== false;
            delete entry._fetchFailed;
            console.log('[Configurator] Preflight recovered stubbed bundle:', bundleHandleForRefresh);
            // Re-fire the unmissable signals downstream now that we have real data.
            updatePricing();
          }
        }).catch(function(err) {
          if (err && err.message === 'SOLD_OUT_PREFLIGHT') throw err;
          // Network/preflight failure: don't block the cart-add; let Shopify be the source of truth.
          // (For a stubbed entry, buildCartItems' variantId guard will catch it and show a clear error.)
          console.warn('[Configurator] Preflight refresh failed (continuing anyway):', err && err.message);
        })
      : Promise.resolve();

    preflight.then(function() {
      // After the bundle-level preflight, refresh per-pick availability and
      // catch any dishes the customer picked that have since sold out — could
      // be a stale localStorage restore, an owner pulling a dish to "out of
      // stock" while the customer was configuring, or a race between picker
      // render and cart-add. Without this the bundle reaches checkout and
      // Shopify rejects it with the unhelpful native out-of-stock modal,
      // listing dishes the customer thought they could buy.
      return refreshPickerDishAvailability();
    }).then(function() {
      var soldOutPicks = findSoldOutPicks();
      if (soldOutPicks.length) {
        console.warn('[Configurator] Picks sold out at cart-add — dropping:', soldOutPicks);
        soldOutPicks.forEach(function(p) { dropPickAndRefreshCard(p.type, p.dish); });
        updateRunningTotal();
        if (typeof refreshAvailabilityUI === 'function') refreshAvailabilityUI();
        persistState();
        var labels = soldOutPicks.map(function(p) { return p.label; });
        var msg = labels.length === 1
          ? labels[0] + ' has just sold out — we\'ve removed it from your Feast. Please pick a replacement and try again.'
          : 'These items have just sold out and have been removed from your Feast: ' + labels.join(', ') + '. Please pick replacements and try again.';
        showWfModal(msg, { title: 'A pick just sold out', cta: 'Got it' });
        if (btn) {
          btn.textContent = originalText;
          btn.disabled = false;
        }
        state.isAddingToCart = false;
        return;
      }

      // Stock ceiling check (Ben 15 Oct 2026). The picker + and the extras step stop at
      // each dish's stock, but the Bestsellers Box composes itself: for a bigger group it
      // can need more of a dish than is in stock. Say so plainly here, instead of letting
      // Shopify reject the bundle and the page quietly fall back to a form post.
      var shortfall = findStockShortfall();
      if (shortfall) {
        var sfMsg = state.tier === 'box'
          ? 'The ' + tierLabel() + ' for ' + state.groupSize + ' needs ' + shortfall.demand + ' of "' + shortfall.label + '" and we only have ' + shortfall.stock + ' this week.\n\nTry a smaller group size, or choose Build Your Own Feast and pick something else.'
          : 'We only have ' + shortfall.stock + ' of "' + shortfall.label + '" this week and your Feast has ' + shortfall.demand + '.\n\nPlease reduce it to ' + shortfall.stock + ' or choose something else.';
        handleSoldOutFailure(sfMsg);
        if (btn) { btn.textContent = originalText; btn.disabled = false; }
        state.isAddingToCart = false;
        return;
      }

      // Build cart items AFTER preflight so stub recovery has populated the
      // real variantId. The variantId guard inside buildCartItems would
      // otherwise reject the stub entry before submission.
      var cartData = buildCartItems();
      if (cartData.error) {
        handleSoldOutFailure(cartData.error);
        if (btn) {
          btn.textContent = originalText;
          btn.disabled = false;
        }
        state.isAddingToCart = false;
        return;
      }
      console.log('[Configurator] Adding to cart:', JSON.stringify(cartData.items, null, 2));
      if (btn) btn.textContent = 'Adding...';
      submitCartItems(cartData, btn, originalText);
    }).catch(function(err) {
      if (err && err.message === 'SOLD_OUT_PREFLIGHT') {
        var label = tierLabel();
        handleSoldOutFailure('The ' + label + ' for ' + state.groupSize + ' just sold out. Try a different group size or path — or hit "Speak To Us" and we can help directly.');
      } else {
        handleSoldOutFailure('Could not add to cart: ' + (err && err.message ? err.message : 'unknown error') + '. Please try again or contact us for help.');
      }
      if (btn) {
        btn.textContent = originalText;
        btn.disabled = false;
      }
      state.isAddingToCart = false;
    });
  }

  // Helpers used by the cart-add flow ────────────────────────────────────────

  // Thin wrapper around the global window.WfModal helper (loaded via
  // snippets/wf-modal.liquid in theme.liquid). Kept here so existing call
  // sites in this file don't need to know whether WfModal is loaded yet —
  // if it isn't (older cached layout, defensive), fall back to native
  // alert so the customer still sees the message.
  function showWfModal(message, opts) {
    if (window.WfModal && typeof window.WfModal.alert === 'function') {
      window.WfModal.alert(message, opts);
    } else {
      alert(message);
    }
  }

  // Single, customer-friendly failure path. Shown when:
  //   - preflight detects the bundle just sold out, or
  //   - Shopify rejects /cart/add with a sold-out / inventory message.
  // Logs to console for owner debugging, then surfaces a styled modal
  // (replacing the old native alert() which felt jarring).
  function handleSoldOutFailure(message) {
    console.warn('[Configurator] Sold-out / cart failure:', message);
    var opts = { title: 'One quick thing', cta: 'Got it' };
    if (/speak to us/i.test(message)) {
      opts.secondaryHref = '/pages/contact';
      opts.secondaryLabel = 'Speak To Us →';
    }
    showWfModal(message, opts);
  }

  // Detect Shopify cart-add responses that effectively mean "sold out" so we
  // can show a tailored message instead of the raw Shopify string. Includes
  // the partial-add quantity-overflow pattern Shopify returns when the
  // requested qty exceeds remaining stock ("Only N items were added to your
  // cart due to availability") — which is the real cause of most customer-
  // reported "sold out when it wasn't" issues, since it fires whenever a
  // bundle's expanded picks bring a dish over its true inventory.
  function isSoldOutMessage(msg) {
    if (!msg) return false;
    var s = String(msg).toLowerCase();
    return s.indexOf('sold out') !== -1
        || s.indexOf('out of stock') !== -1
        || s.indexOf('not available') !== -1
        || s.indexOf('inventory') !== -1
        || s.indexOf('insufficient stock') !== -1
        || s.indexOf('due to availability') !== -1
        || /only\s+\d+\s+(item|items)\s+(was|were)\s+added/i.test(s);
  }

  function submitCartItems(cartData, btn, originalText) {
    // ── Cart-add strategy ──
    // Bundle products tagged "bundle" in Shopify return 422 from /cart/add.js.
    // Try AJAX first (fastest UX); on 422 fall back to form POST which always works.
    // Addons are regular products — always use AJAX.
    var bundleItem = cartData.items[0]; // Always the bundle
    var addonItems = cartData.items.slice(1); // Remaining are addons

    // Step 1: Try adding everything via AJAX.
    // sections=cart-icon-bubble tells Shopify to include a re-rendered
    // header cart icon in the response — we need this because the configurator
    // bypasses Dawn's product-form.js (which is what normally updates the
    // header). Without this, the customer adds an item, sees no UI change,
    // and clicks again (causing a duplicate add).
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: cartData.items, sections: 'cart-icon-bubble' })
    })
    .then(function(response) {
      if (!response.ok) {
        return response.json().then(function(errData) {
          var msg = errData.description || errData.message || '';
          console.warn('[Configurator] AJAX cart add failed (' + response.status + '): ' + msg);
          // 422 on the bundle line → fall back to form POST.
          //
          // Shopify-native bundles always reject /cart/add.js. The wording
          // varies: sometimes the response contains the literal word "bundle",
          // sometimes it's phrased as "the product '<bundle title>' is already
          // sold out" (current /cart/add.js behaviour for these products).
          // Both mean the same thing — use the form-POST path which works for
          // bundle products.
          //
          // We've already pre-flighted `available` via /products/{handle}.js
          // before submitting, so if the storefront said the bundle was
          // available, this 422 is the native-bundle quirk, not a real sold-out.
          var entry = getBundleInfo(state.tier, state.groupSize);
          var bundleTitle = (entry && entry.title) ? String(entry.title).toLowerCase() : '';
          var msgLc = msg.toLowerCase();
          var blamesBundle = bundleTitle && msgLc.indexOf(bundleTitle) !== -1;
          if (response.status === 422 && (msgLc.indexOf('bundle') !== -1 || blamesBundle)) {
            console.log('[Configurator] Bundle product 422 detected, switching to form submission');
            return 'BUNDLE_FALLBACK';
          }
          // 422 with a sold-out / inventory message naming a picker dish:
          // flip just that dish's card and let the customer swap. This path
          // is reached only when the message doesn't blame the bundle (above).
          if (isSoldOutMessage(msg)) {
            // Try to identify which specific picker dish Shopify rejected so we
            // can flip just that card sold-out and tell the customer exactly
            // what to swap — much better UX than "please refresh".
            var soldOutCard = findPickerCardForSoldOutMessage(msg);
            if (soldOutCard) {
              var dishType = soldOutCard.dataset.picker;
              var dishLabel = getDishLabel(soldOutCard.dataset.dish);
              flipPickerCardSoldOut(soldOutCard);
              if (dishType) {
                validateStep(getStepForType(dishType));
                updateRunningTotal();
              }
              updateFooterButton();
              // Shopify's "sold out" message also covers the over-inventory
              // case (you asked for more than we have in stock), not just a
              // genuine 0 stock. Phrase it so both cases read sensibly: we
              // can't tell from the response which one it was.
              handleSoldOutFailure(
                'Couldn\'t add "' + dishLabel + '" to your Feast — we don\'t have enough stock right now for the quantity you picked.\n\nWe\'ve reset that selection. Choose another ' + (dishType || 'option') + ' (or a smaller amount) and click Add to Cart again.'
              );
              throw new Error('SOLD_OUT_PICKER_HANDLED');
            }
            // Couldn't identify the specific picker card. Surface the dish
            // name from the Shopify error (if any) in a friendly message
            // that points the customer at the most common real cause:
            // their cart still has items from an earlier session, so the
            // bundle's expanded picks push that dish over its remaining
            // stock. Asking them to clear the cart fixes it 90% of the time.
            var quotedName = extractQuotedDishName(msg);
            handleSoldOutFailure(
              quotedName
                ? 'We couldn\'t add "' + quotedName + '" to your Feast — it just hit a stock limit.\n\nThis usually happens when something is already in your cart from an earlier visit. Please open your cart, clear it, and try again. If that doesn\'t fix it, hit "Speak To Us" and we\'ll sort it out.'
                : 'One of the dishes in your Feast hit a stock limit during cart-add.\n\nThis usually happens when items are already in your cart from an earlier visit. Please open your cart, clear it, and try again — or hit "Speak To Us" and we can help.'
            );
            throw new Error('SOLD_OUT_FALLBACK_HANDLED');
          }
          // Non-sold-out 422 (some other validation error). Don't leak the
          // raw Shopify message to the customer — show a clear action.
          handleSoldOutFailure(
            'We couldn\'t add your Feast to the cart just now (technical hiccup at the Shopify end). Please refresh the page and try again, or hit "Speak To Us" if it keeps happening.'
          );
          throw new Error('CART_ADD_GENERIC_HANDLED');
        });
      }
      return response.json();
    })
    .then(function(data) {
      if (data === 'BUNDLE_FALLBACK') {
        // Step 2a: Add addons via AJAX first, then bundle via form
        addAddonsAndSubmitBundleForm(addonItems, bundleItem, btn);
        return;
      }
      // AJAX worked — all items added
      console.log('[Configurator] All items added to cart via AJAX:', data);
      if (window.WF && window.WF.event) window.WF.event('addtocart_success', { method: 'ajax', tier: eventTier(), size: state.groupSize, cuisine: state.cuisine || null, date: state.selectedDate || null, cart_value: computeGrandTotalPence() });
      if (btn) btn.textContent = '✓ Added to Cart';
      clearPersistedState();
      document.body.classList.remove('wf-config-active');

      // Refresh the header cart count + show toast ourselves. See the
      // updateHeaderCartBubble / showAddToCartToast comments for why
      // we can't rely on the atc-toast snippet or shop-cart-sync here.
      updateHeaderCartBubble(data && data.sections);
      showAddToCartToast(data);

      // Open the AMP slide cart. SLIDECART_OPENED / SLIDECART_UPDATED are the
      // app's CALLBACKS (we define them), not methods: calling SLIDECART_OPENED
      // here only ran our telemetry and never opened the drawer. The methods
      // are SLIDECART_UPDATE (re-fetch the cart) and SLIDECART_OPEN.
      openSlideCartAfterAdd();

      // Auto-populate Zapiet delivery date with the date already chosen in the configurator
      if (state.selectedDate) {
        window.__wfSelectedDate = state.selectedDate;
        try { sessionStorage.setItem('wf_delivery_date', state.selectedDate); } catch(e) {}
        // Persist as a cart attribute right now so Apple Pay / Shop Pay
        // express buttons (which bypass the standard Checkout-button click
        // handler in layout/theme.liquid) still get the date onto the order.
        if (window.WF_eagerWriteDeliveryDate) window.WF_eagerWriteDeliveryDate(state.selectedDate);
        // Hide Zapiet's visible picker immediately. We already know the date —
        // the configurator captured it. Waiting until the polling loop in
        // SLIDECART_UPDATED confirms a successful write (~5s of stable polls)
        // leaves the duplicate picker visible during that window on the first
        // cart-drawer open. CSS in site-overrides.css hides #storePickupApp
        // when this class is present; the hidden ISO input is still populated
        // by syncZapietDate() so checkout validation continues to work.
        document.body.classList.add('wf-date-prefilled');
        setTimeout(function() { syncZapietDate(state.selectedDate); }, 2000);
      }

      // Re-enable the button so the customer can continue browsing/configuring
      setTimeout(function() {
        if (btn) {
          btn.textContent = originalText;
          btn.disabled = false;
        }
        state.isAddingToCart = false;
      }, 1500);
    })
    .catch(function(err) {
      console.error('[Configurator] Add to cart failed:', err.message);
      if (window.WF && window.WF.event) window.WF.event('addtocart_fail', { reason: (err && err.message) || 'unknown', tier: state.tier, size: state.groupSize, cuisine: state.cuisine || null });
      if (err && (err.message === 'SOLD_OUT_PICKER_HANDLED'
               || err.message === 'SOLD_OUT_FALLBACK_HANDLED'
               || err.message === 'CART_ADD_GENERIC_HANDLED')) {
        // Inner handler already showed the right modal — nothing to do here.
      } else if (err && err.message === 'SOLD_OUT_AT_CART') {
        var label = tierLabel();
        handleSoldOutFailure('The ' + label + ' for ' + state.groupSize + ' is sold out. Try a different group size or path — or hit "Speak To Us" and we can sort it out directly.');
      } else if (isSoldOutMessage(err && err.message)) {
        handleSoldOutFailure('One of the items in your Feast just sold out. Please refresh the page to see what\'s available, or hit "Speak To Us" and we can help.');
      } else {
        handleSoldOutFailure('Could not add to cart: ' + (err && err.message ? err.message : 'unknown error') + '\n\nPlease try again or contact us for help.');
      }
      if (btn) {
        btn.textContent = originalText;
        btn.disabled = false;
      }
      state.isAddingToCart = false;
    });
  }

  // Fallback: add addons via AJAX, then submit bundle via hidden form POST.
  // The form POST mirrors Shopify's own product-page form structure.
  function addAddonsAndSubmitBundleForm(addonItems, bundleItem, btn) {
    // Persist the configurator-selected delivery date BEFORE we navigate.
    // form.submit() below triggers a full-page POST → /cart, which means
    // the AJAX-success branch in submitCartItems() (where sessionStorage
    // normally gets written) never runs. Without this, the /cart page
    // boots with no wf_delivery_date and Zapiet shows a blank input,
    // forcing the customer to re-pick the date they already chose.
    if (state.selectedDate) {
      window.__wfSelectedDate = state.selectedDate;
      try { sessionStorage.setItem('wf_delivery_date', state.selectedDate); } catch (e) {}
      // Persist as a cart attribute now (best-effort; form.submit() below may
      // abort the in-flight fetch). The page-boot hook in layout/theme.liquid
      // re-fires the eager-write from sessionStorage once /cart loads, so the
      // attribute is on the cart before the customer can reach Apple Pay.
      if (window.WF_eagerWriteDeliveryDate) window.WF_eagerWriteDeliveryDate(state.selectedDate);
      // Hide Zapiet's visible picker immediately on the /cart page after the
      // form POST navigates. See matching comment in submitCartItems() success.
      document.body.classList.add('wf-date-prefilled');
    }

    function doFormSubmit() {
      try {
        console.log('[Configurator] Submitting bundle via form POST (variant:', bundleItem.id, ', qty:', bundleItem.quantity, ')');
        // Fire addtocart_success on the form-POST path too — the AJAX path
        // already fires this, but bundle-tagged products go via this fallback
        // (Shopify rejects /cart/add.js for native bundles). Without it the
        // rollup's conversion rate under-counts every bundle add, and the
        // _wf_journey cart attribute (written inside WF.event for this type)
        // never lands on the cart. sendBeacon + keepalive cart-update survive
        // the imminent form.submit() navigation.
        if (window.WF && window.WF.event) {
          window.WF.event('addtocart_success', {
            method: 'form',
            tier: eventTier(),
            size: state.groupSize,
            cuisine: state.cuisine || null,
            date: state.selectedDate || null,
            cart_value: computeGrandTotalPence()
          });
        }
        if (btn) btn.textContent = '✓ Added to Cart';
        clearPersistedState();

        var form = document.createElement('form');
        form.method = 'POST';
        form.action = '/cart/add';
        form.style.display = 'none';

        function addField(name, value) {
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        }

        // Standard Shopify product form fields
        addField('form_type', 'product');
        addField('utf8', '\u2713');
        addField('id', bundleItem.id);
        addField('quantity', bundleItem.quantity);

        // Include product ID (required by Shopify for bundle products).
        // Use tier-aware lookup when bundleHandles is tier-keyed (Cart
        // Transform path); fall back to flat-by-size for legacy structure.
        var bundleInfo;
        if (isTierKeyedBundles) {
          var fbTier = state.tier === 'box' ? 'box' : 'feast';
          bundleInfo = productData.bundles[fbTier] && productData.bundles[fbTier][String(state.groupSize)];
        } else {
          bundleInfo = productData.bundles[String(state.groupSize)];
        }
        if (bundleInfo && bundleInfo.productId) {
          addField('product-id', bundleInfo.productId);
        }

        // Add line-item properties so the kitchen sees the order details
        if (bundleItem.properties) {
          Object.keys(bundleItem.properties).forEach(function(key) {
            addField('properties[' + key + ']', bundleItem.properties[key]);
          });
        }

        // Drop a marker so the /cart page can detect a silent form-POST failure.
        // form.submit() does a full-page nav; if Shopify rejects the add server-side
        // (most likely: one of the dish picks overran its inventory after the Cart
        // Transform expansion), we land on /cart with no bundle line and no error.
        // The /cart-page handler in layout/theme.liquid reads this marker, checks
        // whether the expected bundle landed, and surfaces a clear modal if it didn't.
        try {
          sessionStorage.setItem('wf_pending_bundle_add', JSON.stringify({
            variantId: bundleItem.id,
            productId: bundleInfo && bundleInfo.productId,
            title: bundleInfo && bundleInfo.title,
            sourcePath: location.pathname,
            ts: Date.now()
          }));
        } catch (e) {}

        document.body.appendChild(form);
        form.submit(); // Redirects to /cart
      } catch (formErr) {
        console.error('[Configurator] Form submission failed:', formErr);
        showWfModal('Could not add to cart. Please try again, or hit "Speak To Us" and we\'ll sort it out.', { title: 'Hmm, that didn\'t work', cta: 'Try again', secondaryHref: '/pages/contact', secondaryLabel: 'Speak To Us →' });
        if (btn) {
          btn.textContent = 'Add to Cart →';
          btn.disabled = false;
        }
        state.isAddingToCart = false;
      }
    }

    if (addonItems.length === 0) {
      doFormSubmit();
      return;
    }

    console.log('[Configurator] Adding', addonItems.length, 'addon(s) via AJAX first...');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: addonItems })
    })
    .then(function(response) {
      if (!response.ok) {
        return response.json().then(function(errData) {
          console.warn('[Configurator] Addon AJAX add failed (' + response.status + '): ' + (errData.description || errData.message || 'unknown'));
        }).catch(function() {
          console.warn('[Configurator] Addon AJAX add failed (' + response.status + ')');
        });
      } else {
        console.log('[Configurator] Addons added successfully via AJAX');
      }
    })
    .catch(function(err) {
      console.warn('[Configurator] Addon AJAX request failed:', err.message);
    })
    .finally(function() {
      doFormSubmit();
    });
  }

  // ══════════════════════════════════════════
  // ── SMART FOOTER BUTTON ──
  // Changes text + behaviour based on current step:
  //   Picker steps (incomplete) → disabled "Choose X more..."
  //   Picker steps (complete)  → "Continue to sides →" (advances step)
  //   Addons step              → "Continue to delivery →" (advances step)
  //   Date step (no date)      → disabled "Choose a date to continue"
  //   Date step (date picked)  → "Add to Cart →" (submits)
  // ══════════════════════════════════════════

  var cartBtn = document.querySelector('[data-config-cart]');

  function updateFooterButton() {
    if (!cartBtn) return;
    var step = state.currentStep;
    var type = stepTypeMap[step];

    // Whole-category sold-out: customer can't satisfy this step. Surface
    // an explicit Speak To Us escape hatch instead of "Choose X more...".
    var currentStepEl = document.querySelector('[data-config-step="' + step + '"]');
    if (currentStepEl && currentStepEl.dataset.categorySoldOut === 'true') {
      cartBtn.textContent = 'Speak To Us — every ' + (type || 'option') + ' is sold out';
      cartBtn.disabled = false;
      cartBtn.classList.remove('cuisine-config-add-cart--ready');
      cartBtn.dataset.footerMode = 'speakToUs';
      return;
    }
    cartBtn.dataset.footerMode = '';

    if (type === 'date') {
      // Date step — only show Add to Cart when date is selected
      if (state.selectedDate) {
        cartBtn.textContent = 'Add to Cart \u2192';
        cartBtn.disabled = false;
        cartBtn.classList.add('cuisine-config-add-cart--ready');
      } else {
        cartBtn.textContent = 'Choose a date to continue';
        cartBtn.disabled = true;
        cartBtn.classList.remove('cuisine-config-add-cart--ready');
      }
    } else if (type === 'addons') {
      // Addons step — always allow advancing (addons are optional)
      cartBtn.textContent = 'Continue to delivery \u2192';
      cartBtn.disabled = false;
      cartBtn.classList.remove('cuisine-config-add-cart--ready');
    } else if (pickerTypes.indexOf(type) !== -1) {
      // Picker step — check if complete
      var total = getTotalForType(type);
      var required = getRequiredCount(type);
      if (total >= required) {
        // Find the label for the next step ON THIS PATH (step + 1 would be
        // wrong for any path that skips steps).
        var nextStep = neighbourStep(1);
        var nextType = nextStep === null ? null : stepTypeMap[nextStep];
        var label = 'next step';
        if (nextType === 'main') label = 'mains';
        else if (nextType === 'side') label = 'sides';
        else if (nextType === 'sauce') label = 'sauces';
        else if (nextType === 'bread') label = 'breads';
        else if (nextType === 'addons') label = 'extras';
        else if (nextType === 'date') label = 'delivery';
        cartBtn.textContent = 'Continue to ' + label + ' \u2192';
        cartBtn.disabled = false;
        cartBtn.classList.remove('cuisine-config-add-cart--ready');
      } else {
        var remaining = required - total;
        var typeLabel = type === 'main' ? 'main' : type;
        cartBtn.textContent = 'Choose ' + remaining + ' more ' + typeLabel + (remaining > 1 ? 's' : '') + ' to continue';
        cartBtn.disabled = true;
        cartBtn.classList.remove('cuisine-config-add-cart--ready');
      }
    }
  }

  function footerButtonClick() {
    if (!cartBtn || cartBtn.disabled) return;
    // Whole-category sold-out shortcut: route directly to /pages/contact
    if (cartBtn.dataset.footerMode === 'speakToUs') {
      // Fire telemetry so we can see how often the "every option is sold out"
      // escape hatch is being taken — high frequency = catalogue gap worth
      // addressing. sendBeacon survives the imminent navigation.
      if (window.WF && window.WF.event) {
        window.WF.event('speak_to_us_click', {
          reason: 'category_sold_out',
          step: state.currentStep,
          type: stepTypeMap[state.currentStep] || null,
          cuisine: state.cuisine || null
        });
      }
      window.location.href = '/pages/contact';
      return;
    }
    var type = stepTypeMap[state.currentStep];

    if (type === 'date' && state.selectedDate) {
      // Final step with date — add to cart
      addToCart();
    } else if (type !== 'date') {
      // Advance to the next step on this path
      var next = neighbourStep(1);
      if (next !== null) {
        showStep(next);
        if (configuratorEl) scrollToBuilder();
      }
    }
  }

  if (cartBtn) {
    cartBtn.addEventListener('click', footerButtonClick);
  }

  // ══════════════════════════════════════════
  // ── SELF-TEST (runs in console) ──
  // Accessible via: window.__cuisineConfigTest()
  // ══════════════════════════════════════════

  window.__cuisineConfigTest = function() {
    var results = [];
    var pass = 0, fail = 0;

    function assert(condition, label) {
      if (condition) { results.push('✓ ' + label); pass++; }
      else { results.push('✗ FAIL: ' + label); fail++; }
    }

    assert(dataReady, 'Product data loaded from Shopify API');
    assert(productData.bundles && Object.keys(productData.bundles).length > 0, 'Bundle data: ' + Object.keys(productData.bundles).length + ' top-level keys');
    assert(productData.addons && Object.keys(productData.addons).length > 0, 'Addon data: ' + Object.keys(productData.addons).length + ' addons');

    // Iterate bundles in a structure-aware way: path-keyed checks {feast, box}
    // each containing per-size entries; legacy iterates flat per-size keys directly.
    if (isTierKeyedBundles) {
      ['feast', 'box'].forEach(function(tier) {
        var byTier = productData.bundles[tier] || {};
        Object.keys(configData.bundleHandles[tier] || {}).forEach(function(size) {
          var b = byTier[String(size)];
          assert(b && b.variantId && b.variantId !== 0, tier + ' ' + size + ' people: variant ' + (b ? b.variantId : 'MISSING') + ', ' + (b ? formatPrice(b.price) : '£?'));
          assert(b && b.productId, tier + ' ' + size + ' people: productId ' + (b ? b.productId : 'MISSING'));
        });
      });
    } else {
      Object.keys(configData.bundleHandles).forEach(function(size) {
        var b = productData.bundles[String(size)];
        assert(b && b.variantId && b.variantId !== 0, 'Bundle ' + size + ' people: variant ' + (b ? b.variantId : 'MISSING') + ', ' + (b ? formatPrice(b.price) : '£?'));
        assert(b && b.productId, 'Bundle ' + size + ' people: productId ' + (b ? b.productId : 'MISSING'));
      });
    }

    Object.keys(configData.addonHandles).forEach(function(addon) {
      var a = productData.addons[addon];
      assert(a && a.variantId && a.variantId !== 0, 'Addon "' + addon + '": variant ' + (a ? a.variantId : 'MISSING'));
    });

    // Pricing assertions: iterate all sizes that have at least one bundle loaded
    // (handles tier-keyed by checking each tier's keys, legacy by flat keys).
    var sizesToCheck;
    if (isTierKeyedBundles) {
      var sizeSet = {};
      ['feast', 'box'].forEach(function(tier) {
        Object.keys(productData.bundles[tier] || {}).forEach(function(s) { sizeSet[s] = true; });
      });
      sizesToCheck = Object.keys(sizeSet);
    } else {
      sizesToCheck = Object.keys(productData.bundles);
    }
    sizesToCheck.forEach(function(size) {
      var s = parseInt(size, 10);
      assert(pricing.feast[s] > 0, 'Feast ' + s + ' people: ' + formatPrice(pricing.feast[s]) + '/head');
      if (hasBoxPath) assert(pricing.box[s] > 0, 'Box ' + s + ' people: ' + formatPrice(pricing.box[s]) + '/head');
    });

    assert(configuratorEl, 'Configurator element');
    assert(document.querySelector('[data-config-cart]'), 'Add to Cart button');
    var expectedPathBtns = (hasBoxPath ? 1 : 0) + 1 + (immCard ? 1 : 0);
    assert(document.querySelectorAll('[data-select-tier]').length === expectedPathBtns,
      expectedPathBtns + ' path buttons (' + [hasBoxPath ? 'box' : null, 'build your own', immCard ? 'immersive' : null].filter(Boolean).join(' + ') + ')');
    if (immCard) {
      assert(immPackKey && productData.addons[immPackKey], 'Immersive: Hosting Pack extra "' + immPackKey + '" loaded');
      assert(immPackPrice(state.groupSize) > 0, 'Immersive: Hosting Pack price for ' + state.groupSize + ' people: ' + formatPrice(immPackPrice(state.groupSize) || 0));
    }
    assert(document.querySelectorAll('[data-config-step]').length === totalSteps, 'All ' + totalSteps + ' steps');

    pickerTypes.forEach(function(type) {
      var count = document.querySelectorAll('[data-picker="' + type + '"]').length;
      assert(count >= 1, type + ' pickers: ' + count);
    });

    var fridays = document.querySelectorAll('[data-fri-list] .cuisine-fri-card[data-date]').length;
    assert(fridays > 0, 'Date picker: ' + fridays + ' selectable Fridays');

    assert(Object.keys(stepTypeMap).length === totalSteps, 'Step type map has ' + totalSteps + ' entries');
    assert(pickerTypes.length >= 1, 'At least 1 picker type found: ' + pickerTypes.join(', '));

    console.log('\n══════════════════════════════════');
    console.log('SELF-TEST: ' + pass + ' passed, ' + fail + ' failed');
    console.log('══════════════════════════════════');
    results.forEach(function(r) { console[r.startsWith('✗') ? 'error' : 'log'](r); });
    return { pass: pass, fail: fail };
  };

  // ── Init ──
  updateGroupSizeDisplay();
  renderProgressTicks();
  renderCalendar();
  validateAllSteps();
  // The next-delivery line on the fork cards was removed 2026-09-01 \u2014 Ben:
  // it repeated the dispatch banner sitting directly above the fork. The
  // [data-next-delivery] elements are gone from the section markup.

  fetchProductData();

  console.log('[Configurator] Initialising. Steps:', totalSteps, '| Picker types:', pickerTypes.join(', '), '| Sauce 1:2:', saucePerTwo);

  // ══════════════════════════════════════════
  // ── WF-020: MOBILE UX PROPOSALS (flag-gated) ──
  // Five independent switches, one per proposal sent to Ben 7 Sep. Any
  // subset ships by flipping flags - nothing below runs on desktop, and a
  // false flag leaves the live behaviour untouched.
  // ══════════════════════════════════════════
  // Preview-bar lift (Oct 2026): on an unpublished theme preview Shopify fixes a
  // 68px bar (#PBarNextFrameWrapper) to the bottom of the screen, covering the
  // sticky footer's button. Publish the bar's height as --wf-pbar-h and the footer
  // sits above it. The bar does not exist for real customers, so this is a no-op there.
  (function liftFooterAbovePreviewBar() {
    var root = document.documentElement;
    var last = -1;
    function sync() {
      var bar = document.getElementById('PBarNextFrameWrapper');
      var h = 0;
      if (bar) {
        var r = bar.getBoundingClientRect();
        var cs = window.getComputedStyle(bar);
        if (cs.display !== 'none' && cs.visibility !== 'hidden' && r.height > 0 && r.bottom >= window.innerHeight - 2) h = Math.round(r.height);
      }
      if (h !== last) {
        last = h;
        if (h) root.style.setProperty('--wf-pbar-h', h + 'px'); else root.style.removeProperty('--wf-pbar-h');
      }
    }
    sync();
    window.addEventListener('resize', sync);
    // The bar is injected late and can be dismissed; poll lightly for the first minute.
    var n = 0;
    var t = setInterval(function () { sync(); if (++n > 60) clearInterval(t); }, 1000);
  })();

  var WF_MOBILE_UX = {
    chromeCollapse: false,     // 1. OFF 15 Oct 2026: it hid the Continue / Add to cart button while scrolling down (Ben: button not showing). The phone footer is now compact instead.
    boxAccordion: false,       // 2. OFF 15 Oct 2026 (Ben: "its hard to see what you are getting"): the box contents list stays open
    preForkCompress: true,     // 3. tighten everything above the fork (CSS)
    pickerPhotos: true,        // 4. bigger dish photos on picker rows (CSS)
    deliveryCopyUnified: true  // 5. date step reuses the banner's exact promise
  };
  (function initMobileUx() {
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;
    Object.keys(WF_MOBILE_UX).forEach(function (k) {
      if (WF_MOBILE_UX[k]) document.body.classList.add('wf-mux-' + k.toLowerCase());
    });

    // 1. Scrolling DOWN slims the footer to just the running total (the CTA
    // and free-shipping meter hide); scrolling up, or tapping the slim bar,
    // brings the full footer back. Nothing changes near the top of the page.
    if (WF_MOBILE_UX.chromeCollapse) {
      var muxFooter = document.querySelector('.cuisine-config-footer');
      if (muxFooter) {
        var muxLastY = window.scrollY, muxSlim = false;
        var muxSetSlim = function (v) {
          if (muxSlim === v) return;
          muxSlim = v;
          muxFooter.classList.toggle('is-slim', v);
        };
        window.addEventListener('scroll', function () {
          var y = window.scrollY;
          if (Math.abs(y - muxLastY) < 8) return;
          muxSetSlim(y > muxLastY && y > 300);
          muxLastY = y;
        }, { passive: true });
        muxFooter.addEventListener('click', function (e) {
          if (muxSlim && !e.target.closest('button, a')) muxSetSlim(false);
        });
      }
    }

    // 2. The box contents list (the ~2-screen "What's in the box") collapses
    // behind its own title; tap toggles. The carousel above stays visible.
    if (WF_MOBILE_UX.boxAccordion) {
      var muxBox = document.querySelector('[data-box-contents]');
      var muxBoxTitle = muxBox && muxBox.querySelector('.cuisine-box-contents__title');
      if (muxBox && muxBoxTitle) {
        muxBox.classList.add('is-collapsed');
        muxBoxTitle.setAttribute('role', 'button');
        muxBoxTitle.setAttribute('tabindex', '0');
        muxBoxTitle.setAttribute('aria-expanded', 'false');
        var muxBoxFlip = function () {
          var open = !muxBox.classList.contains('is-open');
          muxBox.classList.toggle('is-open', open);
          muxBox.classList.toggle('is-collapsed', !open);
          muxBoxTitle.setAttribute('aria-expanded', open ? 'true' : 'false');
        };
        muxBoxTitle.addEventListener('click', muxBoxFlip);
        muxBoxTitle.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); muxBoxFlip(); }
        });
      }
    }

    // 5. The date step's delivery paragraph restates the banner's promise in
    // different words. One canonical sentence, same order, same terms.
    if (WF_MOBILE_UX.deliveryCopyUnified) {
      var muxDateHelp = document.querySelector('[data-step-type="date"] .cuisine-config-step__help');
      if (muxDateHelp && !muxDateHelp.classList.contains('cuisine-date-change-note')) {
        muxDateHelp.innerHTML = window.WF_DELIVERY_HELP_HTML ? window.WF_DELIVERY_HELP_HTML() : muxDateHelp.innerHTML;
      }
    }
  })();

})();
